import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Users, Menu as MenuIcon, Package, BarChart, FileText, Settings, Download, Wallet, Smartphone, Coffee, PanelLeftClose, PanelLeft, Search, Receipt, Plus, Trash2, User, LayoutDashboard, Pencil, Loader2, MapPin, Phone, Store, Upload, FileUp, Eye, Image, Crown, Calendar, CreditCard, AlertTriangle, RefreshCw, Key, Mail, Home, IdCard, Check } from 'lucide-react';
import jsPDF from 'jspdf';
import SearchableDropdown from '../components/SearchableDropdown';
import DeviceInfoModal from '../components/DeviceInfoModal';
import CafeExpenseTracker from './CafeExpenseTracker';
import LogoLoader from '../components/LogoLoader';
import RawMaterialsManagement from '../components/RawMaterialsManagement';
import RawMaterialOrders from '../components/RawMaterialOrders';
import LanguageSelector from '../components/LanguageSelector';
import FOSWatermark from '../components/FOSWatermark';
import OnboardingWizard from './OnboardingWizard';
import AdminProfile from './AdminProfile';
import DashboardOverview from '../components/DashboardOverview';
import StaffManagementV2 from '../components/StaffManagementV2';
import AdminInventory from '../components/AdminInventory';
import { performLogout } from '../utils/authUtils';
import GoogleMapsLocationPicker from '../components/GoogleMapsLocationPicker';
import FranchiseDetailsView from '../components/FranchiseDetailsView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [franchises, setFranchises] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [inventory, setInventory] = useState([]);
  const [inventoryTab, setInventoryTab] = useState('catalog');
  const [recipeMenuItemId, setRecipeMenuItemId] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Client logo is only used for PDFs/reports, not in UI
  // Kept for potential future use in report generation
  const [clientLogo, setClientLogo] = useState(null);
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionInfoError, setSubscriptionInfoError] = useState(null);
  const [franchiseSubscriptionInfo, setFranchiseSubscriptionInfo] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentPurpose, setPaymentPurpose] = useState('new'); // 'new' or 'renewal'
  const [renewalFranchiseId, setRenewalFranchiseId] = useState(null);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Get features enabled for this client (subscription-based)
  const features = user?.features_enabled || {};
  
  // Feature check helper - returns true if feature is enabled
  const hasFeature = (featureName) => {
    // Super admin or legacy clients have all features
    if (user?.role === 'super_admin' || Object.keys(features).length === 0) return true;
    
    // Map feature names (some variations exist in database)
    const featureMap = {
      'raw_materials': features.raw_materials === true,
      'reports': features.reports === true,
      'menu_management': features.menu_management === true || features.billing === true, // menu is part of billing
      'inventory_management': features.inventory_management === true || features.billing === true,
      'expense_tracker': features.expense_tracker === true,
      'ledger_management': features.ledger_management === true || features.ledger === true // support both key names
    };
    
    return featureMap[featureName] ?? features[featureName] === true;
  };

  // Helper to get auth headers for API calls
  const getAuthHeaders = () => {
    const token = user?.token || localStorage.getItem('authToken');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };
  
  // Fetch client logo on mount
  useEffect(() => {
    const fetchClientLogo = async () => {
      try {
        const response = await axios.get(`${API}/onboarding/logo`, getAuthHeaders());
        if (response.data.success && response.data.logo_url) {
          setClientLogo(`${BACKEND_URL}${response.data.logo_url}`);
        }
      } catch (error) {
        console.log('Logo not available:', error.message);
      }
    };
    fetchClientLogo();
  }, []);

  const [newFranchise, setNewFranchise] = useState({
    // Section 1: Franchise/Outlet Details
    outlet_name: '',
    outlet_pincode: '',
    outlet_state: '',
    outlet_district: '',
    outlet_city: '',
    outlet_full_address: '',
    outlet_location: null, // {lat, lng} from Google Maps
    is_gst_applicable: false,
    outlet_gstin: '',
    
    // Section 2: Owner/Incharge Details
    owner_full_name: '',
    owner_phone: '',
    owner_email: '',
    owner_full_address: '',
    owner_id_proof_type: '',
    
    // Legacy fields for backward compatibility
    franchise_amount_collected: '',
    date_of_opening: '',
    wifi_password: ''
  });
  const [franchiseDocuments, setFranchiseDocuments] = useState({
    // Section 1 documents
    rent_agreement_doc: null,
    
    // Section 2 documents
    owner_id_proof_doc: null,
    owner_photo: null,
    
    // Section 3: Optional documents
    franchise_agreement_doc: null,
    fssai_license_doc: null,
    trade_license_doc: null,
    gst_certificate_doc: null
  });
  const [uploadingDocument, setUploadingDocument] = useState(null);
  
  // Location data for cascading dropdowns
  const [statesList, setStatesList] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);
  const [loadingPincode, setLoadingPincode] = useState(false);
  
  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${API}/location/states`);
        if (response.data.success) {
          setStatesList(response.data.states);
        }
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    fetchStates();
  }, []);

  // Fetch districts when state changes
  const fetchDistrictsForState = async (state) => {
    if (!state) {
      setDistrictsList([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/location/districts/${encodeURIComponent(state)}`);
      if (response.data.success) {
        setDistrictsList(response.data.districts);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistrictsList([]);
    }
  };

  // Lookup pincode and auto-fill city/taluk
  const lookupPincode = async (pincode, setterFn, currentData) => {
    if (!pincode || pincode.length !== 6) return;
    
    setLoadingPincode(true);
    try {
      const response = await axios.get(`${API}/location/pincode/${pincode}`);
      if (response.data.success) {
        setterFn({
          ...currentData,
          pincode: pincode,
          city: response.data.city || currentData.city,
          taluk: response.data.taluk || currentData.taluk,
          // Auto-update district and state if they match the pincode data
          district: response.data.district || currentData.district,
          state: response.data.state || currentData.state
        });
        
        // If state changed, fetch new districts
        if (response.data.state && response.data.state !== currentData.state) {
          await fetchDistrictsForState(response.data.state);
        }
        
        toast.success('Location auto-filled from pincode');
      }
    } catch (error) {
      console.error('Error looking up pincode:', error);
    } finally {
      setLoadingPincode(false);
    }
  };

  // New pincode lookup for outlet fields (new naming convention)
  const lookupOutletPincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;
    
    setLoadingPincode(true);
    try {
      const response = await axios.get(`${API}/location/pincode/${pincode}`);
      if (response.data.success) {
        setNewFranchise(prev => ({
          ...prev,
          outlet_pincode: pincode,
          outlet_city: response.data.city || prev.outlet_city,
          outlet_district: response.data.district || prev.outlet_district,
          outlet_state: response.data.state || prev.outlet_state
        }));
        toast.success('Location auto-filled from pincode');
      }
    } catch (error) {
      console.error('Error looking up pincode:', error);
    } finally {
      setLoadingPincode(false);
    }
  };

  // Fetch subscription info for the business
  const fetchSubscriptionInfo = async () => {
    setSubscriptionInfoError(null);
    try {
      const response = await axios.get(`${API}/franchise-subscription/info`, getAuthHeaders());
      if (response.data.success) {
        setSubscriptionInfo(response.data);
        return response.data;
      }
      setSubscriptionInfoError('Could not load subscription plan');
    } catch (error) {
      console.error('Error fetching subscription info:', error);
      setSubscriptionInfoError(error.response?.data?.detail || 'Could not load subscription plan');
    }
    return null;
  };

  // Fetch franchise subscription details
  const fetchFranchiseSubscriptionDetails = async (franchiseId) => {
    try {
      const response = await axios.get(`${API}/franchise-subscription/${franchiseId}/details`, getAuthHeaders());
      if (response.data.success) {
        setFranchiseSubscriptionInfo(response.data);
      }
    } catch (error) {
      console.error('Error fetching franchise subscription details:', error);
    }
  };

  // Calculate subscription amount
  const calculateSubscriptionAmount = () => {
    if (!subscriptionInfo) return 0;
    return subscriptionInfo.price_per_franchise * selectedMonths;
  };

  // Open payment modal for new franchise or renewal
  const openPaymentModal = (purpose, franchiseId = null) => {
    setPaymentPurpose(purpose);
    setRenewalFranchiseId(franchiseId);
    setSelectedMonths(1);
    setShowPaymentModal(true);
  };

  // Handle Razorpay payment
  const handlePayment = async () => {
    if (!subscriptionInfo) {
      toast.error('Subscription info not loaded');
      return;
    }

    setIsPaymentProcessing(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setIsPaymentProcessing(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(
        `${API}/franchise-subscription/create-order`,
        {
          franchise_id: renewalFranchiseId,
          num_months: selectedMonths,
          franchise_name: paymentPurpose === 'new' ? newFranchise.outlet_name : null
        },
        getAuthHeaders()
      );

      if (!orderResponse.data.success) {
        toast.error(orderResponse.data.error || 'Failed to create order');
        setIsPaymentProcessing(false);
        return;
      }

      const { order_id, amount, key_id } = orderResponse.data;

      // Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amount * 100,
        currency: 'INR',
        name: 'FOS - Franchise Operating System',
        description: paymentPurpose === 'new' 
          ? `New Franchise Subscription (${selectedMonths} month${selectedMonths > 1 ? 's' : ''})`
          : `Franchise Renewal (${selectedMonths} month${selectedMonths > 1 ? 's' : ''})`,
        order_id: order_id,
        handler: async function (response) {
          await verifyPayment(response, order_id);
        },
        prefill: {
          name: user?.business_name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#D97706'
        },
        modal: {
          ondismiss: function() {
            setIsPaymentProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
      setIsPaymentProcessing(false);
    }
  };

  // Verify payment after Razorpay success
  const verifyPayment = async (razorpayResponse, orderId) => {
    try {
      const verifyData = {
        razorpay_order_id: orderId,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
        franchise_id: renewalFranchiseId,
        num_months: selectedMonths,
        payment_type: paymentPurpose
      };

      // For new franchise, include franchise data with new structure (same as OnboardingWizard)
      if (paymentPurpose === 'new') {
        // Helper to convert file to base64 object
        const fileToBase64 = (file) => {
          return new Promise((resolve, reject) => {
            if (!file) {
              resolve(null);
              return;
            }
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                data: reader.result
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        };

        // Convert documents to base64
        const [
          rent_agreement_doc,
          owner_id_proof_doc,
          owner_photo,
          franchise_agreement_doc,
          fssai_license_doc,
          trade_license_doc,
          gst_certificate_doc
        ] = await Promise.all([
          fileToBase64(franchiseDocuments.rent_agreement_doc),
          fileToBase64(franchiseDocuments.owner_id_proof_doc),
          fileToBase64(franchiseDocuments.owner_photo),
          fileToBase64(franchiseDocuments.franchise_agreement_doc),
          fileToBase64(franchiseDocuments.fssai_license_doc),
          fileToBase64(franchiseDocuments.trade_license_doc),
          fileToBase64(franchiseDocuments.gst_certificate_doc)
        ]);

        verifyData.franchise_data = {
          // Section 1: Franchise Details
          outlet_name: newFranchise.outlet_name,
          outlet_pincode: newFranchise.outlet_pincode,
          outlet_state: newFranchise.outlet_state,
          outlet_district: newFranchise.outlet_district,
          outlet_city: newFranchise.outlet_city,
          outlet_full_address: newFranchise.outlet_full_address,
          outlet_location: newFranchise.outlet_location,
          is_gst_applicable: newFranchise.is_gst_applicable,
          outlet_gstin: newFranchise.outlet_gstin,
          
          // Section 1: Documents
          rent_agreement_doc: rent_agreement_doc,
          
          // Section 2: Owner Details
          owner_full_name: newFranchise.owner_full_name,
          owner_phone: newFranchise.owner_phone,
          owner_email: newFranchise.owner_email,
          owner_full_address: newFranchise.owner_full_address,
          owner_id_proof_type: newFranchise.owner_id_proof_type,
          owner_id_proof_doc: owner_id_proof_doc,
          owner_photo: owner_photo,
          
          // Section 3: Additional Documents
          franchise_agreement_doc: franchise_agreement_doc,
          fssai_license_doc: fssai_license_doc,
          trade_license_doc: trade_license_doc,
          gst_certificate_doc: newFranchise.is_gst_applicable ? gst_certificate_doc : null,
          
          // Additional fields
          franchise_amount_collected: newFranchise.franchise_amount_collected,
          date_of_opening: newFranchise.date_of_opening,
          wifi_password: newFranchise.wifi_password
        };
      }

      const response = await axios.post(
        `${API}/franchise-subscription/verify-payment`,
        verifyData,
        getAuthHeaders()
      );

      if (response.data.success) {
        toast.success(
          paymentPurpose === 'new'
            ? `Franchise onboarded successfully!\nFranchise ID: ${response.data.franchise_id}\nPIN: ${response.data.pin}\nValid until: ${new Date(response.data.subscription_expiry).toLocaleDateString()}${response.data.email_sent ? `\nLogin credentials sent to ${newFranchise.owner_email}` : ''}`
            : `Subscription renewed successfully!\nValid until: ${new Date(response.data.subscription_expiry).toLocaleDateString()}`,
          { duration: 8000 }
        );

        // Reset states and refresh data
        setShowPaymentModal(false);
        setIsAddFranchiseOpen(false);
        
        if (paymentPurpose === 'new') {
          resetFranchiseForm();
        }

        // Refresh franchise list and details
        await fetchFranchises();
        if (renewalFranchiseId) {
          await fetchFranchiseDetails(renewalFranchiseId);
        }
      } else {
        toast.error(response.data.error || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error(error.response?.data?.detail || 'Payment verification failed');
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  // Format expiry date with status
  const formatExpiryDate = (expiryStr) => {
    if (!expiryStr) return { text: 'No subscription', isExpired: true, daysRemaining: null };
    
    const expiry = new Date(expiryStr);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return {
      text: expiry.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      isExpired: diffDays <= 0,
      daysRemaining: diffDays,
      isExpiringSoon: diffDays > 0 && diffDays <= 7
    };
  };

  const [newMenuItem, setNewMenuItem] = useState({ 
    name: '', 
    category: '', 
    price: '', 
    selected_franchises: [],
    // Tax-related fields for Advanced Billing
    tax_rate: 'exempted',
    price_is_inclusive_of_tax: false,
    hsn_sac: ''
  });
  const [editMenuItem, setEditMenuItem] = useState(null);
  const [taxRateOptions] = useState([
    { value: 'exempted', label: 'Exempted (0%)' },
    { value: '5%', label: '5% GST' },
    { value: '12%', label: '12% GST' },
    { value: '18%', label: '18% GST' },
    { value: '28%', label: '28% GST' }
  ]);
  const [isEditMenuOpen, setIsEditMenuOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryModalFor, setCategoryModalFor] = useState('new'); // 'new' or 'edit'
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [newInventory, setNewInventory] = useState({ item_name: '', unit: '' });
  const [reportFilters, setReportFilters] = useState({ franchise_id: 'all', start_date: '', end_date: '' });
  const [isAddFranchiseOpen, setIsAddFranchiseOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isAddInventoryOpen, setIsAddInventoryOpen] = useState(false);
  const [isDeviceInfoOpen, setIsDeviceInfoOpen] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [isFranchiseDetailsOpen, setIsFranchiseDetailsOpen] = useState(false);
  const [isEditingFranchise, setIsEditingFranchise] = useState(false);
  const [editFranchiseData, setEditFranchiseData] = useState(null);
  const [savingFranchise, setSavingFranchise] = useState(false);
  const [actualsData, setActualsData] = useState(null);
  const [actualsFilters, setActualsFilters] = useState({ franchise_id: 'all', start_date: '', end_date: '' });
  const [allLedgers, setAllLedgers] = useState([]);
  const [selectedPartyLedger, setSelectedPartyLedger] = useState(null);
  const [isPartyLedgerDetailsOpen, setIsPartyLedgerDetailsOpen] = useState(false);
  
  // Delete Franchise State
  const [isDeleteFranchiseOpen, setIsDeleteFranchiseOpen] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingFranchise, setDeletingFranchise] = useState(false);
  
  // Change PIN State
  const [isChangePinOpen, setIsChangePinOpen] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [changingPin, setChangingPin] = useState(false);
  
  // Assignment Modal State
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [assignmentTab, setAssignmentTab] = useState('menu'); // 'menu' or 'materials'
  const [assignmentMenuItems, setAssignmentMenuItems] = useState([]);
  const [assignmentRawMaterials, setAssignmentRawMaterials] = useState([]);
  const [selectedMenuItems, setSelectedMenuItems] = useState([]);
  const [selectedRawMaterials, setSelectedRawMaterials] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  
  // Expense Categories State
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [newExpenseCategory, setNewExpenseCategory] = useState('');
  const [franchiseExpenseReports, setFranchiseExpenseReports] = useState([]);
  const [expenseReportFilters, setExpenseReportFilters] = useState({
    franchise_id: 'all',
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const fetchFranchises = async () => {
    try {
      const response = await axios.get(`${API}/franchises`, getAuthHeaders());
      setFranchises(response.data.franchises || []);
    } catch (error) {
      console.error('Error fetching franchises:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`, getAuthHeaders());
      setExpenseCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const handleAddExpenseCategory = async () => {
    if (!newExpenseCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    try {
      await axios.post(`${API}/expense-categories`, { name: newExpenseCategory.trim() });
      toast.success('Expense category added!');
      setNewExpenseCategory('');
      fetchExpenseCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteExpenseCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`);
      toast.success('Category deleted');
      fetchExpenseCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const fetchFranchiseExpenseReports = async () => {
    try {
      const params = {
        start_date: expenseReportFilters.start_date,
        end_date: expenseReportFilters.end_date
      };
      if (expenseReportFilters.franchise_id !== 'all') {
        params.franchise_id = expenseReportFilters.franchise_id;
      }
      const response = await axios.get(`${API}/franchise-expenses/all-reports`, { params, ...getAuthHeaders() });
      setFranchiseExpenseReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching expense reports:', error);
      toast.error('Failed to fetch expense reports');
    }
  };

  // Document upload handler for franchise onboarding
  const handleDocumentUpload = async (docType, file, franchiseId = null) => {
    if (!file) return null;
    
    setUploadingDocument(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      if (franchiseId) {
        formData.append('franchise_id', franchiseId);
      }
      
      const response = await axios.post(
        `${API}/onboarding/upload-document`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        return response.data.document_url;
      }
      return null;
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      toast.error(`Failed to upload ${docType.replace('_', ' ')}`);
      return null;
    } finally {
      setUploadingDocument(null);
    }
  };

  // Upload document for existing franchise (from View Details)
  const uploadDocumentForFranchise = async (docType, file) => {
    if (!file || !selectedFranchise) return;
    
    const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
    setUploadingDocument(docType);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('franchise_id', franchiseId);
      
      const response = await axios.post(
        `${API}/franchises/${franchiseId}/upload-document`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        toast.success('Document uploaded successfully');
        // Refresh franchise details
        await fetchFranchiseDetails(franchiseId);
      }
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      toast.error(`Failed to upload document`);
    } finally {
      setUploadingDocument(null);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`, getAuthHeaders());
      const items = response.data.menu_items || [];
      setMenuItems(items);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory`, getAuthHeaders());
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  // Initial data loading effect - placed after all fetch function declarations
  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      
      // Check onboarding status first - MUST complete before showing anything
      try {
        const token = user?.token || localStorage.getItem('authToken');
        if (token) {
          const response = await axios.get(`${API}/onboarding/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success && !response.data.is_onboarded) {
            setShowOnboarding(true);
            setOnboardingChecked(true);
            setInitialLoading(false);
            return; // Don't load other data if onboarding needed
          }
          setOnboardingChecked(true);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setOnboardingChecked(true); // Still allow proceeding on error
      }
      
      // Only fetch dashboard data after confirming onboarding is complete
      try {
        await Promise.all([
          axios.get(`${API}/franchises`, getAuthHeaders()).then(r => setFranchises(r.data.franchises || [])),
          axios.get(`${API}/menu-items`, getAuthHeaders()).then(r => {
            const items = r.data.menu_items || [];
            setMenuItems(items);
            setCategories([...new Set(items.map(item => item.category).filter(Boolean))]);
          }),
          axios.get(`${API}/inventory`, getAuthHeaders()).then(r => setInventory(r.data.inventory || [])),
          axios.get(`${API}/expense-categories`, getAuthHeaders()).then(r => setExpenseCategories(r.data.categories || [])),
          axios.get(`${API}/franchise-subscription/info`, getAuthHeaders()).then(r => {
            if (r.data.success) setSubscriptionInfo(r.data);
          }).catch((err) => {
            setSubscriptionInfoError(err.response?.data?.detail || 'Could not load subscription plan');
          })
        ]);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
      setInitialLoading(false);
    };
    loadData();
  }, []); // Initial load only - dependencies managed inside loadData

  const fetchFranchiseDetails = async (franchiseId) => {
    try {
      const response = await axios.get(`${API}/franchises/${franchiseId}`, getAuthHeaders());
      setSelectedFranchise(response.data.franchise);
      setEditFranchiseData(null);
      setIsEditingFranchise(false);
      setIsFranchiseDetailsOpen(true);
      
      // Also fetch subscription details
      try {
        const subResponse = await axios.get(`${API}/franchise-subscription/${franchiseId}/details`, getAuthHeaders());
        if (subResponse.data.success) {
          setFranchiseSubscriptionInfo(subResponse.data);
        }
      } catch (subError) {
        console.error('Error fetching franchise subscription:', subError);
        setFranchiseSubscriptionInfo(null);
      }
    } catch (error) {
      console.error('Error fetching franchise details:', error);
      toast.error('Failed to load franchise details');
    }
  };

  const startEditingFranchise = () => {
    const franchiseState = selectedFranchise.state || '';
    
    setEditFranchiseData({
      franchise_name: selectedFranchise.name || selectedFranchise.franchise_name || '',
      franchise_model: selectedFranchise.franchise_model || '',
      franchise_amount: selectedFranchise.franchise_amount || '',
      owner_name: selectedFranchise.owner_name || selectedFranchise.contact_person || '',
      mobile_number: selectedFranchise.mobile_number || selectedFranchise.contact_number || '',
      email: selectedFranchise.email || '',
      aadhaar_no: selectedFranchise.aadhaar_no || '',
      pan_no: selectedFranchise.pan_no || '',
      address_line1: selectedFranchise.address_line1 || '',
      address_line2: selectedFranchise.address_line2 || '',
      city: selectedFranchise.city || '',
      state: franchiseState,
      pincode: selectedFranchise.pincode || '',
      district: selectedFranchise.district || '',
      taluk: selectedFranchise.taluk || '',
      date_of_opening: selectedFranchise.date_of_opening || '',
      manager_name: selectedFranchise.manager_name || '',
      google_maps_link: selectedFranchise.google_maps_link || '',
      additional_owner_names: selectedFranchise.additional_owners || selectedFranchise.additional_owner_names || '',
      wifi_password: selectedFranchise.wifi_password || ''
    });
    
    // Fetch districts for the existing state so the dropdown is populated
    if (franchiseState) {
      fetchDistrictsForState(franchiseState);
    }
    
    setIsEditingFranchise(true);
  };

  const cancelEditFranchise = () => {
    setIsEditingFranchise(false);
    setEditFranchiseData(null);
  };

  const handleUpdateFranchise = async () => {
    if (!editFranchiseData || !selectedFranchise) return;
    
    setSavingFranchise(true);
    try {
      const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
      const response = await axios.put(
        `${API}/franchises/${franchiseId}`,
        editFranchiseData,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success('Franchise updated successfully');
        // Refresh the franchise details
        await fetchFranchiseDetails(franchiseId);
        // Refresh the franchise list
        await fetchFranchises();
        setIsEditingFranchise(false);
      } else {
        toast.error(response.data.error || 'Failed to update franchise');
      }
    } catch (error) {
      console.error('Error updating franchise:', error);
      toast.error(error.response?.data?.detail || 'Failed to update franchise');
    } finally {
      setSavingFranchise(false);
    }
  };

  // Handle Change PIN
  const handleChangePin = async () => {
    if (!newPin || !confirmPin) {
      toast.error('Please enter both PIN fields');
      return;
    }
    
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    
    if (newPin.length < 4 || newPin.length > 6) {
      toast.error('PIN must be 4-6 digits');
      return;
    }
    
    if (!/^\d+$/.test(newPin)) {
      toast.error('PIN must contain only numbers');
      return;
    }
    
    setChangingPin(true);
    try {
      const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
      const response = await axios.put(
        `${API}/franchises/${franchiseId}/change-pin`,
        { new_pin: newPin },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success('PIN changed successfully');
        setIsChangePinOpen(false);
        setNewPin('');
        setConfirmPin('');
        // Refresh franchise details
        await fetchFranchiseDetails(franchiseId);
      } else {
        toast.error(response.data.error || 'Failed to change PIN');
      }
    } catch (error) {
      console.error('Error changing PIN:', error);
      toast.error(error.response?.data?.detail || 'Failed to change PIN');
    } finally {
      setChangingPin(false);
    }
  };

  // ============================================
  // Assignment Functions (Menu Items & Raw Materials)
  // ============================================
  
  const openAssignmentModal = async () => {
    if (!selectedFranchise) return;
    
    const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
    setIsAssignmentModalOpen(true);
    setLoadingAssignments(true);
    setAssignmentTab('menu');
    
    try {
      const response = await axios.get(
        `${API}/franchises/${franchiseId}/assignments`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        const menuItems = response.data.menu_items || [];
        const rawMaterials = response.data.raw_materials || [];
        
        setAssignmentMenuItems(menuItems);
        setAssignmentRawMaterials(rawMaterials);
        
        // Pre-select already assigned items
        setSelectedMenuItems(menuItems.filter(item => item.is_assigned).map(item => item.item_id));
        setSelectedRawMaterials(rawMaterials.filter(item => item.is_assigned).map(item => item.material_id));
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoadingAssignments(false);
    }
  };
  
  const closeAssignmentModal = () => {
    setIsAssignmentModalOpen(false);
    setAssignmentMenuItems([]);
    setAssignmentRawMaterials([]);
    setSelectedMenuItems([]);
    setSelectedRawMaterials([]);
  };
  
  const toggleMenuItemSelection = (itemId) => {
    setSelectedMenuItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };
  
  const toggleRawMaterialSelection = (materialId) => {
    setSelectedRawMaterials(prev => 
      prev.includes(materialId) 
        ? prev.filter(id => id !== materialId)
        : [...prev, materialId]
    );
  };
  
  const selectAllMenuItems = () => {
    const allIds = assignmentMenuItems.map(item => item.item_id);
    setSelectedMenuItems(allIds);
  };
  
  const deselectAllMenuItems = () => {
    setSelectedMenuItems([]);
  };
  
  const selectAllRawMaterials = () => {
    const allIds = assignmentRawMaterials.map(item => item.material_id);
    setSelectedRawMaterials(allIds);
  };
  
  const deselectAllRawMaterials = () => {
    setSelectedRawMaterials([]);
  };
  
  const saveMenuItemAssignments = async () => {
    if (!selectedFranchise) return;
    
    const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
    setSavingAssignments(true);
    
    try {
      const response = await axios.put(
        `${API}/franchises/${franchiseId}/assignments/menu-items`,
        { assigned_item_ids: selectedMenuItems },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success(`${selectedMenuItems.length} menu items assigned to franchise`);
      }
    } catch (error) {
      console.error('Error saving menu item assignments:', error);
      toast.error('Failed to save menu item assignments');
    } finally {
      setSavingAssignments(false);
    }
  };
  
  const saveRawMaterialAssignments = async () => {
    if (!selectedFranchise) return;
    
    const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
    setSavingAssignments(true);
    
    try {
      const response = await axios.put(
        `${API}/franchises/${franchiseId}/assignments/raw-materials`,
        { assigned_material_ids: selectedRawMaterials },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success(`${selectedRawMaterials.length} raw materials assigned to franchise`);
      }
    } catch (error) {
      console.error('Error saving raw material assignments:', error);
      toast.error('Failed to save raw material assignments');
    } finally {
      setSavingAssignments(false);
    }
  };
  
  const saveAllAssignments = async () => {
    setSavingAssignments(true);
    try {
      await saveMenuItemAssignments();
      await saveRawMaterialAssignments();
      toast.success('All assignments saved successfully!');
      closeAssignmentModal();
    } catch (error) {
      console.error('Error saving assignments:', error);
    } finally {
      setSavingAssignments(false);
    }
  };

  // Handle Delete Franchise
  const openDeleteFranchiseDialog = () => {
    setDeleteConfirmStep(1);
    setDeleteConfirmText('');
    setIsDeleteFranchiseOpen(true);
  };

  const closeDeleteFranchiseDialog = () => {
    setIsDeleteFranchiseOpen(false);
    setDeleteConfirmStep(1);
    setDeleteConfirmText('');
  };

  const handleDeleteFranchise = async () => {
    if (!selectedFranchise) return;
    
    const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
    const franchiseName = selectedFranchise.name || selectedFranchise.franchise_name || franchiseId;
    
    setDeletingFranchise(true);
    try {
      const response = await axios.delete(
        `${API}/franchises/${franchiseId}`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success(`Franchise "${franchiseName}" deleted permanently`);
        
        // Close all dialogs
        closeDeleteFranchiseDialog();
        setIsFranchiseDetailsOpen(false);
        setSelectedFranchise(null);
        
        // Refresh franchises list
        fetchFranchises();
      } else {
        toast.error(response.data.error || 'Failed to delete franchise');
      }
    } catch (error) {
      console.error('Error deleting franchise:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete franchise');
    } finally {
      setDeletingFranchise(false);
    }
  };

  const fetchAllLedgers = async () => {
    try {
      const response = await axios.get(`${API}/ledger/parties`, getAuthHeaders());
      setAllLedgers(response.data.parties || []);
    } catch (error) {
      console.error('Error fetching all ledgers:', error);
      toast.error('Failed to load ledgers');
    }
  };

  const viewPartyLedgerDetails = async (party) => {
    setSelectedPartyLedger(party);
    setIsPartyLedgerDetailsOpen(true);
    // Fetch transactions for this party
    try {
      const response = await axios.get(`${API}/ledger/transactions?party_id=${party.party_id}`, getAuthHeaders());
      const transactions = response.data.transactions || [];
      setSelectedPartyLedger({ ...party, transactions });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction history');
    }
  };

  const handleGenerateActualsReport = async () => {
    try {
      const params = new URLSearchParams();
      if (actualsFilters.franchise_id && actualsFilters.franchise_id !== 'all') {
        params.append('franchise_id', actualsFilters.franchise_id);
      }
      if (actualsFilters.start_date) {
        params.append('start_date', actualsFilters.start_date);
      }
      if (actualsFilters.end_date) {
        params.append('end_date', actualsFilters.end_date);
      }

      const response = await axios.get(`${API}/daily-actuals?${params.toString()}`, getAuthHeaders());
      setActualsData(response.data.actuals || []);
      toast.success('Actuals report generated successfully!');
    } catch (error) {
      console.error('Error generating actuals report:', error);
      toast.error('Failed to generate actuals report');
    }
  };

  const handleDownloadReportPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Get franchise details
    const selectedFranchiseData = franchises.find(f => f.id === reportFilters.franchise_id);
    const franchiseName = selectedFranchiseData ? selectedFranchiseData.name : 'All Franchises';
    const franchiseAddress = selectedFranchiseData ? selectedFranchiseData.franchise_address : '';
    const franchiseContact = selectedFranchiseData ? selectedFranchiseData.contact_number : '';
    
    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('1946 Billing System', pageWidth / 2, 20, { align: 'center' });
    
    // Report Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}`, 14, 35);
    doc.text(`Time: ${now.toLocaleTimeString()}`, 14, 40);
    doc.text(`Report Period: ${reportFilters.start_date || 'All'} to ${reportFilters.end_date || 'All'}`, 14, 45);
    
    // Franchise Details
    doc.setFont(undefined, 'bold');
    doc.text('Franchise Details:', 14, 55);
    doc.setFont(undefined, 'normal');
    doc.text(`Name: ${franchiseName}`, 14, 60);
    if (franchiseAddress) doc.text(`Address: ${franchiseAddress}`, 14, 65);
    if (franchiseContact) doc.text(`Contact: ${franchiseContact}`, 14, 70);
    
    // Line separator
    doc.line(14, 75, pageWidth - 14, 75);
    
    // Sales Report Title
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.text('Sales Report', 14, 85);
    
    // Table Headers
    doc.setFontSize(10);
    let yPosition = 95;
    doc.text('Item Name', 14, yPosition);
    doc.text('Price/Unit', 80, yPosition);
    doc.text('Quantity', 120, yPosition);
    doc.text('Total', 160, yPosition, { align: 'right' });
    
    // Line under headers
    doc.line(14, yPosition + 2, pageWidth - 14, yPosition + 2);
    yPosition += 10;
    
    // Table Data with Categories
    doc.setFont(undefined, 'normal');
    if (reportData && reportData.categories) {
      reportData.categories.forEach((category) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Category header
        doc.setFont(undefined, 'bold');
        doc.setFontSize(11);
        doc.text(category.category.toUpperCase(), 14, yPosition);
        yPosition += 8;
        
        // Category items
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        category.items.forEach((item) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text('  ' + item.name, 14, yPosition);
          doc.text(`Rs ${item.price}`, 80, yPosition);
          doc.text(`${item.quantity}`, 120, yPosition);
          doc.text(`Rs ${item.total.toFixed(2)}`, 160, yPosition, { align: 'right' });
          yPosition += 7;
        });
        
        // Category subtotal
        doc.setFont(undefined, 'bold');
        doc.text(`Subtotal (${category.category}):`, 14, yPosition);
        doc.text(`Rs ${category.subtotal.toFixed(2)}`, 160, yPosition, { align: 'right' });
        yPosition += 10;
      });
    }
    
    // Line above grand total
    doc.line(14, yPosition, pageWidth - 14, yPosition);
    yPosition += 8;
    
    // Grand Total
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Grand Total:', 14, yPosition);
    doc.text(`Rs ${reportData?.grand_total?.toFixed(2) || '0.00'}`, 160, yPosition, { align: 'right' });
    
    // Footer
    yPosition = doc.internal.pageSize.getHeight() - 30;
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    doc.text('Thank you for your business!', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;
    doc.setFontSize(8);
    doc.text('* This receipt is for your reference only', pageWidth / 2, yPosition, { align: 'center' });
    
    // Save PDF - Handle both web and Android
    const fileName = `Sales_Report_${franchiseName.replace(/\s+/g, '_')}_${now.toISOString().split('T')[0]}.pdf`;
    
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        // Use downloadService for Android
        const downloadService = (await import('../services/downloadService')).default;
        const pdfBlob = doc.output('blob');
        await downloadService.download({ blob: pdfBlob, filename: fileName, mimeType: 'application/pdf' });
      } else {
        // Web browser - use jsPDF's save
        doc.save(fileName);
        toast.success('Report downloaded successfully!');
      }
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF: ' + error.message);
    }
  };

  const handleAddFranchise = async (e) => {
    e.preventDefault();
    
    // Validate required fields - new structure
    const errors = [];
    if (!newFranchise.outlet_name) errors.push('Outlet Name is required');
    if (!newFranchise.outlet_pincode || newFranchise.outlet_pincode.length !== 6) errors.push('Valid 6-digit Pincode is required');
    if (!newFranchise.outlet_state) errors.push('State is required');
    if (!newFranchise.outlet_district) errors.push('District is required');
    if (!newFranchise.outlet_city) errors.push('City is required');
    if (!newFranchise.outlet_full_address) errors.push('Full Address is required');
    if (!newFranchise.owner_full_name) errors.push('Owner Full Name is required');
    if (!newFranchise.owner_phone) errors.push('Owner Phone is required');
    if (!newFranchise.owner_email) errors.push('Owner Email is required');
    if (!newFranchise.owner_id_proof_type) errors.push('Owner ID Proof Type is required');
    
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    
    // Plan info may have failed to load on page open - retry before deciding the flow
    const planInfo = subscriptionInfo || await fetchSubscriptionInfo();
    if (!planInfo) {
      // Never fall through to payment without knowing the plan - FOS Forever accounts must not be charged
      toast.error('Could not load your subscription plan. Please refresh and try again.');
      return;
    }

    // Check if FOS Forever client - skip payment
    // Server decides via is_forever; plan_id fallback only for an older backend that doesn't send it
    const isForever = typeof planInfo.is_forever === 'boolean'
      ? planInfo.is_forever
      : ['forever', 'fos_forever'].includes(String(planInfo.plan_id || '').toLowerCase());
    if (isForever) {
      await createFranchiseDirectly();
    } else {
      // Open payment modal for regular clients
      openPaymentModal('new');
    }
  };

  // Create franchise directly without payment (for FOS Forever clients)
  const createFranchiseDirectly = async () => {
    try {
      // Helper to convert file to base64 object (same as OnboardingWizard)
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          if (!file) {
            resolve(null);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              data: reader.result
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      // Convert all documents to base64
      const [
        rent_agreement_doc,
        owner_id_proof_doc,
        owner_photo,
        franchise_agreement_doc,
        fssai_license_doc,
        trade_license_doc,
        gst_certificate_doc
      ] = await Promise.all([
        fileToBase64(franchiseDocuments.rent_agreement_doc),
        fileToBase64(franchiseDocuments.owner_id_proof_doc),
        fileToBase64(franchiseDocuments.owner_photo),
        fileToBase64(franchiseDocuments.franchise_agreement_doc),
        fileToBase64(franchiseDocuments.fssai_license_doc),
        fileToBase64(franchiseDocuments.trade_license_doc),
        fileToBase64(franchiseDocuments.gst_certificate_doc)
      ]);

      // Build franchise data using the SAME structure as OnboardingWizard
      const franchiseData = {
        // Section 1: Franchise Details
        outlet_name: newFranchise.outlet_name,
        outlet_pincode: newFranchise.outlet_pincode,
        outlet_state: newFranchise.outlet_state,
        outlet_district: newFranchise.outlet_district,
        outlet_city: newFranchise.outlet_city,
        outlet_full_address: newFranchise.outlet_full_address,
        outlet_location: newFranchise.outlet_location,
        is_gst_applicable: newFranchise.is_gst_applicable,
        outlet_gstin: newFranchise.outlet_gstin,
        
        // Section 1: Documents
        rent_agreement_doc: rent_agreement_doc,
        
        // Section 2: Owner Details
        owner_full_name: newFranchise.owner_full_name,
        owner_phone: newFranchise.owner_phone,
        owner_email: newFranchise.owner_email,
        owner_full_address: newFranchise.owner_full_address,
        owner_id_proof_type: newFranchise.owner_id_proof_type,
        owner_id_proof_doc: owner_id_proof_doc,
        owner_photo: owner_photo,
        
        // Section 3: Additional Documents
        franchise_agreement_doc: franchise_agreement_doc,
        fssai_license_doc: fssai_license_doc,
        trade_license_doc: trade_license_doc,
        gst_certificate_doc: newFranchise.is_gst_applicable ? gst_certificate_doc : null,
        
        // Additional fields
        franchise_amount_collected: newFranchise.franchise_amount_collected,
        date_of_opening: newFranchise.date_of_opening,
        wifi_password: newFranchise.wifi_password
      };
      
      // Step 1: Create franchise using the SAME endpoint as OnboardingWizard
      const createResponse = await axios.post(
        `${API}/onboarding/franchise`,
        franchiseData,
        getAuthHeaders()
      );
      
      if (!createResponse.data.success) {
        throw new Error(createResponse.data.error || 'Failed to create franchise');
      }
      
      const franchiseId = createResponse.data.franchise_id;
      
      // Step 2: Immediately activate (go-live) since this is direct creation
      const goLiveResponse = await axios.post(
        `${API}/onboarding/franchise/${franchiseId}/go-live`,
        {},
        getAuthHeaders()
      );
      
      if (!goLiveResponse.data.success) {
        throw new Error(goLiveResponse.data.error || 'Failed to activate franchise');
      }
      
      toast.success(
        `Franchise onboarded successfully!\nFranchise ID: ${franchiseId}\nPIN: ${createResponse.data.pin}\n${goLiveResponse.data.email_sent ? `Login credentials sent to ${newFranchise.owner_email}` : ''}`, 
        { duration: 8000 }
      );
      
      // Reset form
      resetFranchiseForm();
      setIsAddFranchiseOpen(false);
      fetchFranchises();
    } catch (error) {
      console.error('Error creating franchise:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to onboard franchise');
    }
  };

  // Helper to reset franchise form
  const resetFranchiseForm = () => {
    setNewFranchise({
      outlet_name: '',
      outlet_pincode: '',
      outlet_state: '',
      outlet_district: '',
      outlet_city: '',
      outlet_full_address: '',
      outlet_location: null,
      is_gst_applicable: false,
      outlet_gstin: '',
      owner_full_name: '',
      owner_phone: '',
      owner_email: '',
      owner_full_address: '',
      owner_id_proof_type: '',
      franchise_amount_collected: '',
      date_of_opening: '',
      wifi_password: ''
    });
    setFranchiseDocuments({
      rent_agreement_doc: null,
      owner_id_proof_doc: null,
      owner_photo: null,
      franchise_agreement_doc: null,
      fssai_license_doc: null,
      trade_license_doc: null,
      gst_certificate_doc: null
    });
  };

  const handleAddMenuItem = async (e) => {
    e.preventDefault();
    
    // Validate category is selected
    if (!newMenuItem.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const selectedFranchises = newMenuItem.selected_franchises.length > 0 
        ? newMenuItem.selected_franchises 
        : franchises.map(f => f.id);
      
      await axios.post(`${API}/menu-items`, { 
        name: newMenuItem.name,
        category: newMenuItem.category,
        price: parseFloat(newMenuItem.price),
        selected_franchises: selectedFranchises,
        // Tax-related fields for Advanced Billing
        tax_rate: newMenuItem.tax_rate || 'exempted',
        price_is_inclusive_of_tax: newMenuItem.price_is_inclusive_of_tax || false,
        hsn_sac: newMenuItem.hsn_sac || ''
      }, getAuthHeaders());
      toast.success('Menu item added successfully!');
      setNewMenuItem({ 
        name: '', 
        category: '', 
        price: '', 
        selected_franchises: [],
        tax_rate: 'exempted',
        price_is_inclusive_of_tax: false,
        hsn_sac: ''
      });
      setIsAddMenuOpen(false);
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to add menu item');
    }
  };

  const handleEditMenuItem = async (e) => {
    e.preventDefault();
    
    // Validate category is selected
    if (!editMenuItem.category) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      // Prepare image data if present
      let imageData = editMenuItem.image || null;
      
      // If there's a new image file, convert to base64
      if (editMenuItem.imageFile) {
        const file = editMenuItem.imageFile;
        
        // Check file size (max 1MB)
        if (file.size > 1024 * 1024) {
          toast.error('Image size must be less than 1MB');
          return;
        }
        
        // Convert to base64
        imageData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              data: reader.result
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      
      await axios.put(`${API}/menu-items/${editMenuItem.id}`, {
        name: editMenuItem.name,
        category: editMenuItem.category,
        price: parseFloat(editMenuItem.price),
        is_active: true,
        selected_franchises: editMenuItem.selected_franchises || [],
        // Tax-related fields for Advanced Billing
        tax_rate: editMenuItem.tax_rate || 'exempted',
        price_is_inclusive_of_tax: editMenuItem.price_is_inclusive_of_tax || false,
        hsn_sac: editMenuItem.hsn_sac || '',
        image: imageData
      }, getAuthHeaders());
      toast.success('Menu item updated successfully!');
      setEditMenuItem(null);
      setIsEditMenuOpen(false);
      fetchMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update menu item');
    }
  };

  const handleDeleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }
    try {
      await axios.delete(`${API}/menu-items/${itemId}`);
      toast.success('Menu item deleted successfully!');
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const handleToggleFranchise = (franchiseId) => {
    setNewMenuItem(prev => {
      const selected = prev.selected_franchises || [];
      if (selected.includes(franchiseId)) {
        return { ...prev, selected_franchises: selected.filter(id => id !== franchiseId) };
      } else {
        return { ...prev, selected_franchises: [...selected, franchiseId] };
      }
    });
  };

  const handleSelectAllFranchises = () => {
    setNewMenuItem(prev => ({
      ...prev,
      selected_franchises: franchises.map(f => f.id)
    }));
  };

  const handleDeselectAllFranchises = () => {
    setNewMenuItem(prev => ({ ...prev, selected_franchises: [] }));
  };

  const handleAddInventory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory`, newInventory);
      toast.success('Inventory item added successfully!');
      setNewInventory({ item_name: '', unit: '' });
      setIsAddInventoryOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to add inventory item');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const filters = {
        ...reportFilters,
        franchise_id: reportFilters.franchise_id || 'all'
      };
      const response = await axios.post(`${API}/reports/sales`, filters, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Map backend response to expected frontend format
      const data = response.data;
      
      // Convert by_franchise to franchise_breakdown with correct field names
      if (data.by_franchise && data.by_franchise.length > 0) {
        data.franchise_breakdown = data.by_franchise.map(f => ({
          franchise_id: f.franchise_id,
          franchise_name: f.franchise_name || f.franchise_id,
          total_orders: f.order_count,
          total_revenue: f.total_sales
        }));
      }
      
      setReportData(data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleLogout = () => {
    // Use performLogout which clears all user data and does a full page reload
    // This prevents data leakage between different user accounts
    performLogout(setUser, null, '/');
  };

  const handleInitializeSheets = async () => {
    try {
      await axios.post(`${API}/initialize-sheets`);
      toast.success('Sheets initialized with sample data!');
      fetchMenuItems();
      fetchInventory();
    } catch (error) {
      toast.error('Failed to initialize sheets');
    }
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Reload data after onboarding
    fetchFranchises();
    fetchMenuItems();
    fetchInventory();
    toast.success('Welcome to FOS! Your setup is complete.');
  };

  // Loading Gate - Show loading screen until onboarding status is checked
  if (!onboardingChecked || initialLoading) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
        <LogoLoader size="lg" text="Loading..." />
      </div>
    );
  }

  // Show onboarding wizard if not onboarded
  if (showOnboarding) {
    return <OnboardingWizard user={user} onComplete={handleOnboardingComplete} />;
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar - Responsive & Collapsible */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-50
        ${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-surface border-r border-border flex flex-col shadow-lg
        transform transition-all duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className={`${isSidebarCollapsed ? 'p-2' : 'p-4'} border-b border-border flex-shrink-0`}>
          <div className="flex justify-center mb-2">
            {/* Always use FOS logo in sidebar - business logo is for reports/PDFs only */}
            <img 
              src="/fos-logo.png" 
              alt="FOS Logo" 
              className={`${isSidebarCollapsed ? 'h-10 w-10' : 'h-14 sm:h-16'} w-auto object-contain rounded-lg`}
            />
          </div>
          {!isSidebarCollapsed && (
            <>
              <h1 className="text-xl font-bold text-primary text-center">Admin Portal</h1>
              <p className="text-sm text-secondary mt-1 text-center">HQ Dashboard</p>
              {/* Subscription Badge */}
              {subscriptionInfo && (
                <div className="mt-3 flex justify-center">
                  <span className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
                    ${(subscriptionInfo.plan_id === 'forever' || subscriptionInfo.is_forever)
                      ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 text-white animate-pulse' 
                      : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white'}
                  `}>
                    <Crown className="h-3.5 w-3.5" />
                    {subscriptionInfo.plan_name || 'FOS Pro'}
                  </span>
                </div>
              )}
            </>
          )}
          {/* Collapsed state badge */}
          {isSidebarCollapsed && subscriptionInfo && (
            <div className="mt-2 flex justify-center" title={subscriptionInfo.plan_name || 'FOS Pro'}>
              <span className={`
                inline-flex items-center justify-center w-8 h-8 rounded-full
                ${(subscriptionInfo.plan_id === 'forever' || subscriptionInfo.is_forever)
                  ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 text-white animate-pulse' 
                  : 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white'}
              `}>
                <Crown className="h-4 w-4" />
              </span>
            </div>
          )}
        </div>
        <nav className={`flex-1 ${isSidebarCollapsed ? 'p-2' : 'p-4'} space-y-2 overflow-y-auto`}>
          <Button
            data-testid="dashboard-nav"
            variant={activeSection === 'dashboard' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('dashboard')}
            title="Dashboard"
          >
            <LayoutDashboard className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Dashboard'}
          </Button>
          <Button
            data-testid="franchises-nav"
            variant={activeSection === 'franchises' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('franchises')}
            title="Franchises"
          >
            <Users className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Franchises'}
          </Button>
          {hasFeature('menu_management') && (
            <Button
              data-testid="menu-nav"
              variant={activeSection === 'menu' ? 'default' : 'ghost'}
              className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
              onClick={() => setActiveSection('menu')}
              title="Menu Items"
            >
              <MenuIcon className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isSidebarCollapsed && 'Menu Items'}
            </Button>
          )}
          {hasFeature('inventory_management') && (
            <Button
              data-testid="inventory-nav"
              variant={activeSection === 'inventory' ? 'default' : 'ghost'}
              className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
              onClick={() => setActiveSection('inventory')}
              title="Inventory"
            >
              <Package className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isSidebarCollapsed && 'Inventory'}
            </Button>
          )}
          {hasFeature('reports') && (
            <Button
              data-testid="reports-nav"
              variant={activeSection === 'reports' ? 'default' : 'ghost'}
              className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
              onClick={() => setActiveSection('reports')}
              title="Sales Reports"
            >
              <BarChart className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isSidebarCollapsed && 'Sales Reports'}
            </Button>
          )}
          
          {/* TEMPORARILY HIDDEN - Franchise Expenses (not production ready) */}
          {false && hasFeature('expense_tracker') && (
            <Button
              data-testid="franchise-expenses-nav"
              variant={activeSection === 'franchise-expenses' ? 'default' : 'ghost'}
              className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
              onClick={() => setActiveSection('franchise-expenses')}
              title="Franchise Expenses"
            >
              <Receipt className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isSidebarCollapsed && 'Franchise Expenses'}
            </Button>
          )}
          
          {/* TEMPORARILY HIDDEN - Franchise Actuals (not production ready) */}
          {false && hasFeature('reports') && (
            <Button
              data-testid="actuals-nav"
              variant={activeSection === 'actuals' ? 'default' : 'ghost'}
              className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
              onClick={() => setActiveSection('actuals')}
              title="Franchise Actuals"
            >
              <FileText className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isSidebarCollapsed && 'Franchise Actuals'}
            </Button>
          )}
          
          {hasFeature('ledger_management') && (
            <Button
              data-testid="ledgers-nav"
              variant={activeSection === 'ledgers' ? 'default' : 'ghost'}
              className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
              onClick={() => setActiveSection('ledgers')}
              title="Party Ledgers"
            >
              <Wallet className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
              {!isSidebarCollapsed && 'Party Ledgers'}
            </Button>
          )}
          
          {/* TEMPORARILY HIDDEN - Setup (not production ready) */}
          {false && (
          <Button
            data-testid="setup-nav"
            variant={activeSection === 'setup' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('setup')}
            title="Setup"
          >
            <Settings className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Setup'}
          </Button>
          )}
          
          {/* Staff & Payroll Section */}
          <Button
            data-testid="staff-payroll-nav"
            variant={activeSection === 'staff-payroll' ? 'default' : 'ghost'}
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
            onClick={() => setActiveSection('staff-payroll')}
            title="Staff & Payroll"
          >
            <Users className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
            {!isSidebarCollapsed && 'Staff & Payroll'}
          </Button>
          
          {/* Raw Materials Section - only if feature enabled */}
          {hasFeature('raw_materials') && (
            <>
              <Button
                data-testid="raw-materials-nav"
                variant={activeSection === 'raw-materials' ? 'default' : 'ghost'}
                className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
                onClick={() => setActiveSection('raw-materials')}
                title="Raw Materials"
              >
                <Package className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
                {!isSidebarCollapsed && 'Raw Materials'}
              </Button>
              <Button
                data-testid="raw-material-orders-nav"
                variant={activeSection === 'raw-material-orders' ? 'default' : 'ghost'}
                className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
                onClick={() => setActiveSection('raw-material-orders')}
                title="RM Orders"
              >
                <Receipt className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
                {!isSidebarCollapsed && 'RM Orders'}
              </Button>
            </>
          )}
          
          {/* Cafe 1946 - Only for specific client */}
          {user?.client_id === 'FOS-260130-3581-001' && (
            <>
              <div className="my-3 border-t border-border" />
              <Button
                data-testid="cafe-nav"
                variant={activeSection === 'cafe' ? 'default' : 'ghost'}
                className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-12 rounded-xl font-medium`}
                onClick={() => setActiveSection('cafe')}
                title="Cafe 1946"
              >
                <Coffee className={`${isSidebarCollapsed ? '' : 'mr-3'} h-5 w-5`} />
                {!isSidebarCollapsed && 'Cafe 1946'}
              </Button>
            </>
          )}
        </nav>
        
        {/* Collapse Toggle Button */}
        <div className={`${isSidebarCollapsed ? 'p-2' : 'p-4'} border-t border-border flex-shrink-0 hidden lg:block`}>
          <Button
            variant="ghost"
            className={`w-full ${isSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} h-10 rounded-xl`}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isSidebarCollapsed ? (
              <PanelLeft className="h-5 w-5" />
            ) : (
              <>
                <PanelLeftClose className="mr-3 h-5 w-5" />
                Collapse
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content - Responsive */}
      <div className="flex-1 overflow-auto">
        {/* Header Bar - All Screens */}
        <div className="sticky top-0 z-30 bg-surface border-b border-border">
          <div className="px-4 py-3 flex items-center justify-between">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 lg:hidden"
            >
              <MenuIcon className="h-6 w-6" />
            </Button>
            
            {/* Logo & Title (Mobile Only) - Always use FOS logo */}
            <div className="flex items-center gap-2 lg:hidden">
              <img 
                src="/fos-logo.png" 
                alt="FOS Logo" 
                className="h-10 w-auto object-contain rounded"
              />
            </div>

            {/* Spacer for desktop */}
            <div className="hidden lg:block flex-1"></div>
            
            {/* Profile Button */}
            <Button
              data-testid="admin-profile-button"
              variant="ghost"
              size="sm"
              onClick={() => setActiveSection('profile')}
              className={`p-2 rounded-full transition-colors group ${activeSection === 'profile' ? 'bg-amber-100' : 'hover:bg-amber-50'}`}
              title="Profile & Settings"
            >
              <User className={`h-5 w-5 ${activeSection === 'profile' ? 'text-amber-600' : 'text-secondary group-hover:text-amber-600'}`} />
            </Button>
            
            {/* Language Selector */}
            <LanguageSelector variant="dropdown" />
            
            {/* Logout Button - Always Visible */}
            <Button
              data-testid="admin-logout-button"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="p-2 hover:bg-red-50 rounded-full transition-colors group"
              title="Logout"
            >
              <LogOut className="h-5 w-5 text-secondary group-hover:text-red-600" />
            </Button>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
        {activeSection === 'dashboard' && (
          <DashboardOverview 
            user={user} 
            franchises={franchises}
            onNavigate={(section) => setActiveSection(section)}
          />
        )}
        
        {activeSection === 'profile' && (
          <AdminProfile onBack={() => setActiveSection('dashboard')} />
        )}
        
        {activeSection === 'franchises' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary">Franchises</h2>
              <Dialog open={isAddFranchiseOpen} onOpenChange={setIsAddFranchiseOpen}>
                <DialogTrigger asChild>
                  <Button className="h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-4 sm:px-6 font-bold text-sm sm:text-base uppercase tracking-wider w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    Onboard Franchise
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-primary">Onboard New Franchise</DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">Add a new outlet to your business</p>
                  </DialogHeader>
                  
                  {/* FOS Forever Badge - No Payment Required */}
                  {(subscriptionInfo?.plan_id === 'forever' || subscriptionInfo?.is_forever) && (
                    <div className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-amber-500/10 border border-purple-500/30 rounded-xl p-4 mt-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 rounded-full">
                          <Crown className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-purple-700">FOS Forever VIP</p>
                          <p className="text-sm text-gray-600">No payment required - Unlimited franchise onboarding included!</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleAddFranchise} className="space-y-6 mt-4">
                    
                    {/* Section 1: Franchise/Outlet Details */}
                    <div className="bg-muted/30 rounded-xl p-4 space-y-4">
                      <h3 className="font-semibold text-lg text-primary flex items-center gap-2">
                        <Store className="w-5 h-5" />
                        Franchise Details
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="outlet_name">Outlet Name *</Label>
                          <Input
                            id="outlet_name"
                            value={newFranchise.outlet_name}
                            onChange={(e) => setNewFranchise({ ...newFranchise, outlet_name: e.target.value })}
                            placeholder="e.g., Main Branch, MG Road Outlet"
                            className="h-10"
                            required
                          />
                        </div>
                      </div>

                      {/* Pincode-first Address Flow */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="outlet_pincode">Pincode *</Label>
                          <div className="relative">
                            <Input
                              id="outlet_pincode"
                              value={newFranchise.outlet_pincode}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setNewFranchise({ ...newFranchise, outlet_pincode: value });
                                if (value.length === 6) {
                                  lookupOutletPincode(value);
                                }
                              }}
                              placeholder="560001"
                              maxLength={6}
                              className="h-10"
                              required
                            />
                            {loadingPincode && (
                              <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="outlet_state">State *</Label>
                          <Input
                            id="outlet_state"
                            value={newFranchise.outlet_state}
                            onChange={(e) => setNewFranchise({ ...newFranchise, outlet_state: e.target.value })}
                            placeholder="Auto-filled from pincode"
                            className="h-10 bg-gray-50"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="outlet_district">District *</Label>
                          <Input
                            id="outlet_district"
                            value={newFranchise.outlet_district}
                            onChange={(e) => setNewFranchise({ ...newFranchise, outlet_district: e.target.value })}
                            placeholder="Auto-filled from pincode"
                            className="h-10 bg-gray-50"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="outlet_city">City / Town *</Label>
                        <Input
                          id="outlet_city"
                          value={newFranchise.outlet_city}
                          onChange={(e) => setNewFranchise({ ...newFranchise, outlet_city: e.target.value })}
                          placeholder="City or town name"
                          className="h-10"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="outlet_full_address">Full Address *</Label>
                        <textarea
                          id="outlet_full_address"
                          value={newFranchise.outlet_full_address}
                          onChange={(e) => setNewFranchise({ ...newFranchise, outlet_full_address: e.target.value })}
                          placeholder="Complete address including building name, street, landmarks"
                          className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required
                        />
                      </div>

                      {/* Google Maps Location Picker */}
                      <div>
                        <Label>Select Location on Map</Label>
                        <div className="mt-2">
                          <GoogleMapsLocationPicker
                            value={newFranchise.outlet_location}
                            onChange={(location) => setNewFranchise({ ...newFranchise, outlet_location: location })}
                            pincode={newFranchise.outlet_pincode}
                            city={newFranchise.outlet_city}
                            height="300px"
                          />
                        </div>
                      </div>

                      {/* Rent Agreement Upload */}
                      <div className="border rounded-lg p-3 space-y-2">
                        <Label className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Rent Agreement / Address Proof
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, rent_agreement_doc: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.rent_agreement_doc && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">Max 10MB (PDF, JPG, PNG)</p>
                      </div>

                      {/* GST Toggle */}
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is_gst_applicable"
                          checked={newFranchise.is_gst_applicable || false}
                          onChange={(e) => setNewFranchise({ ...newFranchise, is_gst_applicable: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor="is_gst_applicable">GST Applicable for this outlet</Label>
                      </div>
                      {newFranchise.is_gst_applicable && (
                        <div>
                          <Label htmlFor="outlet_gstin">GSTIN</Label>
                          <Input
                            id="outlet_gstin"
                            value={newFranchise.outlet_gstin || ''}
                            onChange={(e) => setNewFranchise({ ...newFranchise, outlet_gstin: e.target.value.toUpperCase() })}
                            placeholder="22AAAAA0000A1Z5"
                            maxLength={15}
                            className="h-10"
                          />
                        </div>
                      )}
                    </div>

                    {/* Section 2: Owner / Incharge Details */}
                    <div className="bg-muted/30 rounded-xl p-4 space-y-4">
                      <h3 className="font-semibold text-lg text-primary flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Owner / Incharge Details
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="owner_full_name">Full Name *</Label>
                          <Input
                            id="owner_full_name"
                            value={newFranchise.owner_full_name}
                            onChange={(e) => setNewFranchise({ ...newFranchise, owner_full_name: e.target.value })}
                            placeholder="Owner's full name"
                            className="h-10"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="owner_phone">Phone Number *</Label>
                          <Input
                            id="owner_phone"
                            type="tel"
                            value={newFranchise.owner_phone}
                            onChange={(e) => setNewFranchise({ ...newFranchise, owner_phone: e.target.value })}
                            placeholder="+91 9876543210"
                            className="h-10"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="owner_email">Email Address *</Label>
                          <Input
                            id="owner_email"
                            type="email"
                            value={newFranchise.owner_email}
                            onChange={(e) => setNewFranchise({ ...newFranchise, owner_email: e.target.value })}
                            placeholder="owner@example.com"
                            className="h-10"
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-1">Login credentials will be sent to this email</p>
                        </div>
                        <div>
                          <Label htmlFor="owner_id_proof_type">Identity Proof Type *</Label>
                          <Select
                            value={newFranchise.owner_id_proof_type || ''}
                            onValueChange={(value) => setNewFranchise({ ...newFranchise, owner_id_proof_type: value })}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="Select ID type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="aadhaar">Aadhaar Card</SelectItem>
                              <SelectItem value="pan">PAN Card</SelectItem>
                              <SelectItem value="driving_license">Driving License</SelectItem>
                              <SelectItem value="voter_id">Voter ID</SelectItem>
                              <SelectItem value="passport">Passport</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="owner_full_address">Owner's Full Address</Label>
                        <textarea
                          id="owner_full_address"
                          value={newFranchise.owner_full_address}
                          onChange={(e) => setNewFranchise({ ...newFranchise, owner_full_address: e.target.value })}
                          placeholder="Owner's residential address"
                          className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                        />
                      </div>

                      {/* Owner Documents */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border rounded-lg p-3 space-y-2">
                          <Label className="flex items-center gap-2">
                            <IdCard className="w-4 h-4" />
                            Upload Identity Proof
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, owner_id_proof_doc: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.owner_id_proof_doc && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>
                        <div className="border rounded-lg p-3 space-y-2">
                          <Label className="flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            Passport Size Photo
                          </Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, owner_photo: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.owner_photo && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Upload Documents (Optional) */}
                    <div className="bg-muted/30 rounded-xl p-4 space-y-4">
                      <h3 className="font-semibold text-lg text-primary flex items-center gap-2">
                        <FileUp className="w-5 h-5" />
                        Upload Documents (Optional)
                      </h3>
                      <p className="text-sm text-muted-foreground">These documents can be uploaded later from franchise settings</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border rounded-lg p-3 space-y-2">
                          <Label className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Franchise Agreement
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx,image/*"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, franchise_agreement_doc: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.franchise_agreement_doc && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>

                        <div className="border rounded-lg p-3 space-y-2">
                          <Label className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            FSSAI License
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx,image/*"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, fssai_license_doc: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.fssai_license_doc && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>

                        <div className="border rounded-lg p-3 space-y-2">
                          <Label className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Trade License
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx,image/*"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, trade_license_doc: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.trade_license_doc && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>

                        <div className="border rounded-lg p-3 space-y-2">
                          <Label className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            GST Certificate
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,.doc,.docx,image/*"
                            onChange={(e) => setFranchiseDocuments({...franchiseDocuments, gst_certificate_doc: e.target.files[0]})}
                            className="h-9 text-sm"
                          />
                          {franchiseDocuments.gst_certificate_doc && (
                            <span className="text-xs text-green-600">✓ Selected</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Max file size: 10MB per file (PDF, JPG, PNG)</p>
                    </div>

                    {/* Info Alert */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> A unique Franchise ID and 5-digit PIN will be auto-generated. 
                        Login credentials will be emailed to the owner.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddFranchiseOpen(false)}
                        className="flex-1 h-12 rounded-full font-bold"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className={`flex-1 h-12 text-white rounded-full font-bold ${
                          subscriptionInfo?.plan_id === 'forever' || subscriptionInfo?.is_forever
                            ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 hover:opacity-90'
                            : 'bg-primary hover:bg-primary/90'
                        }`}
                      >
                        {subscriptionInfo?.plan_id === 'forever' || subscriptionInfo?.is_forever
                          ? 'Create Franchise (Free)'
                          : 'Proceed to Payment'
                        }
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {franchises.map((franchise) => (
                <Card key={franchise.id} data-testid={`franchise-card-${franchise.id}`} className="shadow-sm border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg sm:text-xl font-bold text-primary truncate" title={franchise.name}>
                      {franchise.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Franchise ID:</span>
                      <span className="font-mono ml-1 text-primary font-semibold">{franchise.franchise_id || franchise.id}</span>
                    </p>
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Location:</span> {franchise.location || franchise.address?.city || franchise.city || 'Not set'}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Contact:</span> {franchise.contact_person || franchise.owner_name || franchise.contact || 'Not set'}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary">
                      <span className="font-medium">Phone:</span> {franchise.contact_number || franchise.mobile_number || 'Not set'}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary mb-3">
                      <span className="font-medium">PIN:</span> <span className="font-mono">{franchise.pin_code || franchise.pin || franchise.passcode || 'N/A'}</span>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                      <Button
                        onClick={() => fetchFranchiseDetails(franchise.id)}
                        className="flex-1 h-9 sm:h-10 bg-accent text-white hover:bg-accent/90 rounded-full font-bold text-xs sm:text-sm"
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedFranchise(franchise);
                          setIsDeviceInfoOpen(true);
                        }}
                        variant="outline"
                        className="h-9 sm:h-10 px-3 bg-blue-50 hover:bg-blue-100 border-blue-200 rounded-full sm:w-auto"
                        title="View Device Info"
                      >
                        <Smartphone className="h-4 w-4" />
                        <span className="ml-2 sm:hidden">Device Info</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'menu' && (
          <div>
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-primary">Menu Items</h2>
                <Dialog open={isAddMenuOpen} onOpenChange={setIsAddMenuOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-menu-item-button" className="h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-4 sm:px-6 font-bold text-sm sm:text-base uppercase tracking-wider w-full sm:w-auto">
                      Add Menu Item
                    </Button>
                  </DialogTrigger>
              <DialogContent className="bg-surface max-w-md sm:max-w-lg">
                <DialogHeader className="pb-2">
                  <DialogTitle className="text-xl sm:text-2xl font-bold">Add New Menu Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddMenuItem} className="space-y-3 sm:space-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-1">
                  <div>
                    <Label htmlFor="menu-name" className="text-sm">Name</Label>
                    <Input
                      id="menu-name"
                      data-testid="menu-name-input"
                      value={newMenuItem.name}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })}
                      className="h-10 sm:h-12 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="menu-category" className="text-sm">Category <span className="text-red-500">*</span></Label>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-10 sm:h-12 rounded-xl justify-between"
                      onClick={() => {
                        setCategoryModalFor('new');
                        setIsCategoryModalOpen(true);
                      }}
                    >
                      <span className={newMenuItem.category ? "text-primary" : "text-gray-500"}>
                        {newMenuItem.category || "Select category"}
                      </span>
                      <MenuIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <Label htmlFor="menu-price" className="text-sm">Price (₹)</Label>
                    <Input
                      id="menu-price"
                      data-testid="menu-price-input"
                      type="number"
                      step="0.01"
                      value={newMenuItem.price}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, price: e.target.value })}
                      className="h-10 sm:h-12 rounded-xl"
                      required
                    />
                  </div>
                  {/* Tax Configuration Section */}
                  <div className="border border-border rounded-xl p-3 sm:p-4 bg-muted/30">
                    <Label className="text-xs sm:text-sm font-semibold text-primary mb-2 sm:mb-3 block">Tax Configuration (GST)</Label>
                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      <div>
                        <Label htmlFor="menu-tax-rate" className="text-xs">Tax Rate</Label>
                        <Select
                          value={newMenuItem.tax_rate}
                          onValueChange={(value) => setNewMenuItem({ ...newMenuItem, tax_rate: value })}
                        >
                          <SelectTrigger className="h-9 sm:h-10 rounded-lg text-xs sm:text-sm">
                            <SelectValue placeholder="Select tax rate" />
                          </SelectTrigger>
                          <SelectContent>
                            {taxRateOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="menu-hsn" className="text-xs">HSN/SAC Code</Label>
                        <Input
                          id="menu-hsn"
                          data-testid="menu-hsn-input"
                          value={newMenuItem.hsn_sac}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, hsn_sac: e.target.value })}
                          className="h-9 sm:h-10 rounded-lg text-xs sm:text-sm"
                          placeholder="e.g., 996331"
                        />
                      </div>
                    </div>
                    <div className="mt-2 sm:mt-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newMenuItem.price_is_inclusive_of_tax}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, price_is_inclusive_of_tax: e.target.checked })}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-xs sm:text-sm">Price is inclusive of tax</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-sm">Assign to Franchises</Label>
                      <div className="space-x-1 sm:space-x-2">
                        <Button type="button" variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={handleSelectAllFranchises}>Select All</Button>
                        <Button type="button" variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={handleDeselectAllFranchises}>Deselect All</Button>
                      </div>
                    </div>
                    <div className="max-h-32 sm:max-h-40 overflow-y-auto border rounded-xl p-2 sm:p-3 space-y-1 sm:space-y-2">
                      {franchises.map((franchise) => (
                        <label key={franchise.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(newMenuItem.selected_franchises || []).includes(franchise.id)}
                            onChange={() => handleToggleFranchise(franchise.id)}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-xs sm:text-sm">{franchise.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-secondary mt-1 sm:mt-2">
                      {newMenuItem.selected_franchises.length === 0 
                        ? "All franchises selected by default" 
                        : `${newMenuItem.selected_franchises.length} franchise(s) selected`}
                    </p>
                  </div>
                  <Button type="submit" data-testid="submit-menu-item-button" className="w-full h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider text-sm">
                    Add Menu Item
                  </Button>
                </form>
              </DialogContent>
              </Dialog>
              </div>
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search menu items by name or category..."
                  value={menuSearchQuery}
                  onChange={(e) => setMenuSearchQuery(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {/* Sort menu items alphabetically by category, then by name, and filter by search */}
              {[...menuItems]
                .sort((a, b) => {
                  const categoryCompare = (a.category || '').localeCompare(b.category || '');
                  if (categoryCompare !== 0) return categoryCompare;
                  return (a.name || '').localeCompare(b.name || '');
                })
                .filter(item => {
                  if (!menuSearchQuery.trim()) return true;
                  const query = menuSearchQuery.toLowerCase();
                  return (
                    (item.name || '').toLowerCase().includes(query) ||
                    (item.category || '').toLowerCase().includes(query)
                  );
                })
                .map((item) => (
                <Card key={item.id} data-testid={`menu-item-card-${item.id}`} className="shadow-sm border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-primary mb-2">{item.name}</h3>
                    <p className="text-xs text-secondary uppercase tracking-wider mb-2">{item.category}</p>
                    <p className="text-2xl font-bold text-accent mb-1">₹{item.price}</p>
                    {/* Tax info display */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        item.tax_rate === 'exempted' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                      }`}>
                        {item.tax_rate === 'exempted' 
                          ? 'Tax Exempt' 
                          : item.price_is_inclusive_of_tax 
                            ? `incl ${item.tax_rate} GST`
                            : `+${item.tax_rate} GST`
                        }
                      </span>
                      {item.hsn_sac && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                          HSN: {item.hsn_sac}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          // Convert franchises string to selected_franchises array
                          const franchisesStr = item.franchises || '';
                          const selectedFranchises = franchisesStr ? franchisesStr.split(',').filter(f => f.trim()) : [];
                          setEditMenuItem({
                            ...item,
                            selected_franchises: selectedFranchises
                          });
                          setIsEditMenuOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleDeleteMenuItem(item.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Edit Menu Item Dialog */}
            <Dialog open={isEditMenuOpen} onOpenChange={setIsEditMenuOpen}>
              <DialogContent className="bg-surface max-w-md sm:max-w-lg">
                <DialogHeader className="pb-2">
                  <DialogTitle className="text-xl sm:text-2xl font-bold">Edit Menu Item</DialogTitle>
                </DialogHeader>
                {editMenuItem && (
                  <form onSubmit={handleEditMenuItem} className="space-y-3 sm:space-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-1">
                    {/* Menu Item Image Upload */}
                    <div>
                      <Label className="text-sm">Menu Item Image</Label>
                      <div className="mt-2 flex items-start gap-4">
                        {/* Image Preview */}
                        <div className="w-24 h-24 border-2 border-dashed border-border rounded-xl flex items-center justify-center overflow-hidden bg-muted/30">
                          {(editMenuItem.imageFile || editMenuItem.image?.data) ? (
                            <img 
                              src={editMenuItem.imageFile 
                                ? URL.createObjectURL(editMenuItem.imageFile) 
                                : editMenuItem.image?.data}
                              alt="Menu item"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground text-center px-2">No image</span>
                          )}
                        </div>
                        {/* Upload Controls */}
                        <div className="flex-1 space-y-2">
                          <label className="cursor-pointer">
                            <div className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                              <Upload className="w-4 h-4" />
                              <span className="text-sm">Upload Image</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  if (file.size > 1024 * 1024) {
                                    toast.error('Image must be less than 1MB');
                                    return;
                                  }
                                  setEditMenuItem({ ...editMenuItem, imageFile: file });
                                }
                              }}
                            />
                          </label>
                          {(editMenuItem.imageFile || editMenuItem.image?.data) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => setEditMenuItem({ ...editMenuItem, imageFile: null, image: null })}
                            >
                              Remove Image
                            </Button>
                          )}
                          <p className="text-xs text-muted-foreground">Max 1MB. JPG, PNG, WebP</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-menu-name" className="text-sm">Name</Label>
                      <Input
                        id="edit-menu-name"
                        value={editMenuItem.name}
                        onChange={(e) => setEditMenuItem({ ...editMenuItem, name: e.target.value })}
                        className="h-10 sm:h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-menu-category" className="text-sm">Category <span className="text-red-500">*</span></Label>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-10 sm:h-12 rounded-xl justify-between"
                        onClick={() => {
                          setCategoryModalFor('edit');
                          setIsCategoryModalOpen(true);
                        }}
                      >
                        <span className={editMenuItem.category ? "text-primary" : "text-gray-500"}>
                          {editMenuItem.category || "Select category"}
                        </span>
                        <MenuIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <Label htmlFor="edit-menu-price" className="text-sm">Price (₹)</Label>
                      <Input
                        id="edit-menu-price"
                        type="number"
                        step="0.01"
                        value={editMenuItem.price}
                        onChange={(e) => setEditMenuItem({ ...editMenuItem, price: e.target.value })}
                        className="h-10 sm:h-12 rounded-xl"
                        required
                      />
                    </div>
                    {/* Tax Configuration Section */}
                    <div className="border border-border rounded-xl p-3 sm:p-4 bg-muted/30">
                      <Label className="text-xs sm:text-sm font-semibold text-primary mb-2 sm:mb-3 block">Tax Configuration (GST)</Label>
                      <div className="grid grid-cols-2 gap-2 sm:gap-4">
                        <div>
                          <Label htmlFor="edit-menu-tax-rate" className="text-xs">Tax Rate</Label>
                          <Select
                            value={editMenuItem.tax_rate || 'exempted'}
                            onValueChange={(value) => setEditMenuItem({ ...editMenuItem, tax_rate: value })}
                          >
                            <SelectTrigger className="h-9 sm:h-10 rounded-lg text-xs sm:text-sm">
                              <SelectValue placeholder="Select tax rate" />
                            </SelectTrigger>
                            <SelectContent>
                              {taxRateOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="edit-menu-hsn" className="text-xs">HSN/SAC Code</Label>
                          <Input
                            id="edit-menu-hsn"
                            data-testid="edit-menu-hsn-input"
                            value={editMenuItem.hsn_sac || ''}
                            onChange={(e) => setEditMenuItem({ ...editMenuItem, hsn_sac: e.target.value })}
                            className="h-9 sm:h-10 rounded-lg text-xs sm:text-sm"
                            placeholder="e.g., 996331"
                          />
                        </div>
                      </div>
                      <div className="mt-2 sm:mt-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editMenuItem.price_is_inclusive_of_tax || false}
                            onChange={(e) => setEditMenuItem({ ...editMenuItem, price_is_inclusive_of_tax: e.target.checked })}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-xs sm:text-sm">Price is inclusive of tax</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Assign to Franchises</Label>
                      <div className="max-h-32 sm:max-h-40 overflow-y-auto border border-border rounded-xl p-2 sm:p-3 mt-2 space-y-1 sm:space-y-2">
                        {franchises.map((franchise) => (
                          <div key={franchise.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`edit-franchise-${franchise.id}`}
                              checked={editMenuItem.selected_franchises?.includes(franchise.id) || false}
                              onChange={(e) => {
                                const selected = editMenuItem.selected_franchises || [];
                                if (e.target.checked) {
                                  setEditMenuItem({
                                    ...editMenuItem,
                                    selected_franchises: [...selected, franchise.id]
                                  });
                                } else {
                                  setEditMenuItem({
                                    ...editMenuItem,
                                    selected_franchises: selected.filter(id => id !== franchise.id)
                                  });
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <label htmlFor={`edit-franchise-${franchise.id}`} className="text-xs sm:text-sm">
                              {franchise.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setRecipeMenuItemId(editMenuItem?.item_id || editMenuItem?.id);
                        setInventoryTab('recipes');
                        setEditMenuItem(null);
                        setActiveSection('inventory');
                      }}
                      className="w-full text-center text-sm text-primary hover:text-primary/80 underline underline-offset-2 py-1"
                      data-testid="menu-edit-recipe-link"
                    >
                      Set up recipe / ingredients for this item →
                    </button>
                    <Button type="submit" className="w-full h-10 sm:h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider text-sm">
                      Update Menu Item
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeSection === 'inventory' && (
          <AdminInventory getAuthHeaders={getAuthHeaders} franchises={franchises} initialTab={inventoryTab} initialMenuItemId={recipeMenuItemId} />
        )}

        {activeSection === 'reports' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Sales Reports</h2>
            <Card className="mb-6 shadow-sm border-border">
              <CardContent className="p-6">
                {/* Quick Date Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setReportFilters({ ...reportFilters, start_date: today, end_date: today });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      const dateStr = yesterday.toISOString().split('T')[0];
                      setReportFilters({ ...reportFilters, start_date: dateStr, end_date: dateStr });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Yesterday
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const last7Days = new Date();
                      last7Days.setDate(today.getDate() - 7);
                      setReportFilters({
                        ...reportFilters,
                        start_date: last7Days.toISOString().split('T')[0],
                        end_date: today.toISOString().split('T')[0]
                      });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                      setReportFilters({
                        ...reportFilters,
                        start_date: firstDay.toISOString().split('T')[0],
                        end_date: today.toISOString().split('T')[0]
                      });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setReportFilters({ franchise_id: 'all', start_date: '', end_date: '' })}
                    className="h-10 rounded-full font-bold"
                  >
                    Clear
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label htmlFor="report-franchise">Franchise</Label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setReportFilters({ ...reportFilters, franchise_id: 'all' })}
                        className={`absolute right-2 top-2 z-20 text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                          reportFilters.franchise_id === 'all' 
                            ? 'bg-accent text-white' 
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        All
                      </button>
                      <SearchableDropdown
                        options={franchises}
                        value={reportFilters.franchise_id === 'all' ? '' : reportFilters.franchise_id}
                        onChange={(value) => setReportFilters({ ...reportFilters, franchise_id: value || 'all' })}
                        placeholder="Search franchise by name or city..."
                        getOptionLabel={(f) => `${f.name} - ${f.location}`}
                        getOptionValue={(f) => f.id}
                        searchKeys={['name', 'location', 'id']}
                        className="relative"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="report-start-date">Start Date</Label>
                    <Input
                      id="report-start-date"
                      data-testid="report-start-date-input"
                      type="date"
                      value={reportFilters.start_date}
                      onChange={(e) => setReportFilters({ ...reportFilters, start_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="report-end-date">End Date</Label>
                    <Input
                      id="report-end-date"
                      data-testid="report-end-date-input"
                      type="date"
                      value={reportFilters.end_date}
                      onChange={(e) => setReportFilters({ ...reportFilters, end_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
                <Button data-testid="generate-report-button" onClick={handleGenerateReport} className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                  Generate Report
                </Button>
              </CardContent>
            </Card>

            {reportData && (
              <Card className="shadow-sm border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl font-bold text-primary">Sales Report</CardTitle>
                  <Button
                    onClick={handleDownloadReportPDF}
                    className="h-10 bg-accent text-white hover:bg-accent/90 rounded-full px-4 font-bold flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </CardHeader>
                <CardContent>
                  {/* Franchise-wise breakdown when "All" is selected */}
                  {reportFilters.franchise_id === 'all' && reportData.franchise_breakdown && reportData.franchise_breakdown.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Franchise-wise Sales Summary
                      </h3>
                      <div className="space-y-3">
                        {reportData.franchise_breakdown.map((franchise, idx) => (
                          <div key={franchise.franchise_id || idx} className="p-4 bg-gradient-to-r from-amber-50 to-white rounded-xl border border-amber-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-bold text-primary text-lg">{franchise.franchise_name}</h4>
                                <p className="text-sm text-secondary">{franchise.total_orders} orders</p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-amber-600">₹{franchise.total_revenue.toLocaleString('en-IN', {minimumFractionDigits: 2})}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 p-4 bg-amber-100 rounded-xl border-2 border-amber-300">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-primary text-lg">Total ({reportData.franchise_breakdown.length} franchises with sales)</span>
                          <span className="text-2xl font-bold text-amber-700">₹{reportData.grand_total?.toLocaleString('en-IN', {minimumFractionDigits: 2}) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Category-wise breakdown */}
                  {reportData.categories && reportData.categories.length > 0 ? (
                    <div className="space-y-6 mb-6">
                      <h3 className="text-lg font-bold text-primary flex items-center gap-2">
                        <MenuIcon className="h-5 w-5" />
                        Category-wise Breakdown
                      </h3>
                      {reportData.categories.map((category, catIndex) => (
                        <div key={catIndex} className="space-y-3">
                          <h4 className="text-lg font-bold text-primary uppercase tracking-wide border-b-2 border-accent pb-2">
                            {category.category}
                          </h4>
                          {category.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex justify-between items-center p-3 bg-muted rounded-lg ml-4">
                              <div className="flex-1">
                                <p className="font-bold text-primary">{item.name}</p>
                                <p className="text-sm text-secondary">₹{item.price}/unit × {item.quantity} units</p>
                              </div>
                              <p className="text-xl font-bold text-accent">₹{item.total.toFixed(2)}</p>
                            </div>
                          ))}
                          <div className="flex justify-between items-center p-3 bg-accent/10 rounded-lg ml-4 border-l-4 border-accent">
                            <p className="font-bold text-primary">Subtotal ({category.category})</p>
                            <p className="text-lg font-bold text-accent">₹{category.subtotal.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-secondary py-8">No sales data for selected period</p>
                  )}
                  <div className="border-t-2 border-primary pt-4">
                    <div className="flex justify-between items-center">
                      <p className="text-xl font-bold text-primary">Grand Total:</p>
                      <p className="text-3xl font-bold text-accent" data-testid="report-grand-total">
                        ₹{reportData.grand_total ? reportData.grand_total.toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Franchise Expenses Section */}
        {activeSection === 'franchise-expenses' && (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-primary">Franchise Expenses</h2>
            
            {/* Expense Categories Management */}
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Manage Expense Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-4">
                  <Input
                    placeholder="New category name..."
                    value={newExpenseCategory}
                    onChange={(e) => setNewExpenseCategory(e.target.value)}
                    className="max-w-xs"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddExpenseCategory()}
                  />
                  <Button onClick={handleAddExpenseCategory} className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {expenseCategories.length === 0 ? (
                    <p className="text-secondary text-sm">No expense categories. Add some above.</p>
                  ) : (
                    expenseCategories.map(cat => (
                      <div key={cat.category_id} className="flex items-center gap-2 bg-muted px-3 py-2 rounded-full">
                        <span className="text-sm font-medium">{cat.name}</span>
                        <button
                          onClick={() => handleDeleteExpenseCategory(cat.category_id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Franchise Expense Reports */}
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart className="h-5 w-5" />
                  Franchise Expense Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <Label>Franchise</Label>
                    <Select
                      value={expenseReportFilters.franchise_id}
                      onValueChange={(value) => setExpenseReportFilters(prev => ({ ...prev, franchise_id: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select franchise" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Franchises</SelectItem>
                        {franchises.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={expenseReportFilters.start_date}
                      onChange={(e) => setExpenseReportFilters(prev => ({ ...prev, start_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={expenseReportFilters.end_date}
                      onChange={(e) => setExpenseReportFilters(prev => ({ ...prev, end_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={fetchFranchiseExpenseReports} className="w-full bg-amber-600 hover:bg-amber-700">
                      Generate Report
                    </Button>
                  </div>
                </div>

                {franchiseExpenseReports.length > 0 ? (
                  <div className="space-y-4">
                    {franchiseExpenseReports.map((report, index) => (
                      <Card key={index} className="border">
                        <CardHeader className="py-3 bg-muted/50">
                          <CardTitle className="text-lg flex items-center justify-between">
                            <span>{report.franchise_name}</span>
                            <span className="text-red-600 font-bold">₹{report.total_expenses?.toFixed(2) || '0.00'}</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="py-3">
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div className="bg-green-50 p-3 rounded-lg">
                              <p className="text-sm text-green-700">Cash Expenses</p>
                              <p className="text-xl font-bold text-green-600">₹{report.cash_expenses?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <p className="text-sm text-blue-700">Online Expenses</p>
                              <p className="text-xl font-bold text-blue-600">₹{report.online_expenses?.toFixed(2) || '0.00'}</p>
                            </div>
                          </div>
                          {report.category_breakdown?.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Category Breakdown:</p>
                              <div className="flex flex-wrap gap-2">
                                {report.category_breakdown.map((cat, catIndex) => (
                                  <div key={catIndex} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                                    {cat.category}: <span className="font-bold">₹{cat.total?.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-secondary py-8">Click &quot;Generate Report&quot; to view franchise expense reports</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeSection === 'actuals' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Franchise Actuals</h2>
            <Card className="mb-6 shadow-sm border-border">
              <CardContent className="p-6">
                {/* Quick Date Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <Button variant="outline" onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    setActualsFilters({ ...actualsFilters, start_date: today, end_date: today });
                  }} className="h-10 rounded-full font-bold">Today</Button>
                  <Button variant="outline" onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const dateStr = yesterday.toISOString().split('T')[0];
                    setActualsFilters({ ...actualsFilters, start_date: dateStr, end_date: dateStr });
                  }} className="h-10 rounded-full font-bold">Yesterday</Button>
                  <Button variant="outline" onClick={() => {
                    const today = new Date();
                    const last7Days = new Date();
                    last7Days.setDate(today.getDate() - 7);
                    setActualsFilters({...actualsFilters, start_date: last7Days.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0]});
                  }} className="h-10 rounded-full font-bold">Last 7 Days</Button>
                  <Button variant="outline" onClick={() => {
                    const today = new Date();
                    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                    setActualsFilters({...actualsFilters, start_date: firstDay.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0]});
                  }} className="h-10 rounded-full font-bold">This Month</Button>
                  <Button variant="outline" onClick={() => setActualsFilters({ franchise_id: 'all', start_date: '', end_date: '' })} className="h-10 rounded-full font-bold">Clear</Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label htmlFor="actuals-franchise">Franchise</Label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setActualsFilters({ ...actualsFilters, franchise_id: 'all' })}
                        className={`absolute right-2 top-2 z-20 text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                          actualsFilters.franchise_id === 'all' 
                            ? 'bg-accent text-white' 
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        All
                      </button>
                      <SearchableDropdown
                        options={franchises}
                        value={actualsFilters.franchise_id === 'all' ? '' : actualsFilters.franchise_id}
                        onChange={(value) => setActualsFilters({ ...actualsFilters, franchise_id: value || 'all' })}
                        placeholder="Search franchise by name or city..."
                        getOptionLabel={(f) => `${f.name} - ${f.location}`}
                        getOptionValue={(f) => f.id}
                        searchKeys={['name', 'location', 'id']}
                        className="relative"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="actuals-start-date">Start Date</Label>
                    <Input id="actuals-start-date" type="date" value={actualsFilters.start_date} onChange={(e) => setActualsFilters({ ...actualsFilters, start_date: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="actuals-end-date">End Date</Label>
                    <Input id="actuals-end-date" type="date" value={actualsFilters.end_date} onChange={(e) => setActualsFilters({ ...actualsFilters, end_date: e.target.value })} className="h-12 rounded-xl" />
                  </div>
                </div>
                <Button onClick={handleGenerateActualsReport} className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                  Generate Actuals Report
                </Button>
              </CardContent>
            </Card>

            {actualsData && (
              <Card className="shadow-sm border-border">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-primary">Actuals Data</CardTitle>
                </CardHeader>
                <CardContent>
                  {actualsData.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-muted">
                            <th className="border border-border p-3 text-left font-bold">Date & Time</th>
                            <th className="border border-border p-3 text-left font-bold">Franchise Name</th>
                            <th className="border border-border p-3 text-right font-bold">Turnover (₹)</th>
                            <th className="border border-border p-3 text-right font-bold">Milk Consumed (L)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actualsData.map((actual, index) => (
                            <tr key={index} className="hover:bg-muted/50">
                              <td className="border border-border p-3">{new Date(actual.date_time).toLocaleString()}</td>
                              <td className="border border-border p-3">{actual.franchise_name}</td>
                              <td className="border border-border p-3 text-right font-bold">₹{parseFloat(actual.turnover).toFixed(2)}</td>
                              <td className="border border-border p-3 text-right">{parseFloat(actual.milk_consumed).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-accent text-white font-bold">
                            <td className="border border-border p-3" colSpan="2">Total</td>
                            <td className="border border-border p-3 text-right">
                              ₹{actualsData.reduce((sum, actual) => sum + parseFloat(actual.turnover), 0).toFixed(2)}
                            </td>
                            <td className="border border-border p-3 text-right">
                              {actualsData.reduce((sum, actual) => sum + parseFloat(actual.milk_consumed), 0).toFixed(2)} L
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-secondary py-8">No actuals data for selected period</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeSection === 'ledgers' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">Party Ledgers - All Franchises</h2>
            <Card className="mb-6 shadow-sm border-border">
              <CardContent className="p-6">
                <Button
                  data-testid="load-ledgers-button"
                  onClick={fetchAllLedgers}
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider"
                >
                  Load All Ledgers
                </Button>
              </CardContent>
            </Card>

            {allLedgers.length > 0 && (
              <Card className="shadow-sm border-border">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-primary">All Party Ledgers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-muted">
                          <th className="border border-border p-3 text-left font-bold">Party Name</th>
                          <th className="border border-border p-3 text-left font-bold">Phone Number</th>
                          <th className="border border-border p-3 text-left font-bold">Franchise</th>
                          <th className="border border-border p-3 text-right font-bold">Balance</th>
                          <th className="border border-border p-3 text-center font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allLedgers.map((ledger, index) => (
                          <tr key={index} className="hover:bg-muted/50">
                            <td className="border border-border p-3 font-medium">{ledger.party_name}</td>
                            <td className="border border-border p-3">{ledger.phone_number}</td>
                            <td className="border border-border p-3">{ledger.franchise_name}</td>
                            <td className={`border border-border p-3 text-right font-bold ${
                              parseFloat(ledger.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              ₹{parseFloat(ledger.balance).toFixed(2)}
                            </td>
                            <td className="border border-border p-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-full"
                                onClick={() => viewPartyLedgerDetails(ledger)}
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeSection === 'setup' && (
          <div>
            <h2 className="text-3xl font-bold text-primary mb-6">System Setup</h2>
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-primary">Initialize Google Sheets</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-secondary mb-4">This will create sample menu items and inventory in your Google Sheets database. Run this after setting up your service account credentials.</p>
                <Button data-testid="initialize-sheets-button" onClick={handleInitializeSheets} className="h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-6 font-bold uppercase tracking-wider">
                  Initialize Sheets
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Staff & Payroll Section */}
        {activeSection === 'staff-payroll' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-primary">Staff & Payroll Management</h2>
              <p className="text-muted-foreground mt-1">Manage staff across all franchises, configure shifts, salary, and generate payroll</p>
            </div>
            <div className="mb-4">
              <Label className="text-sm font-medium">Select Franchise</Label>
              <Select 
                value={selectedFranchise?.franchise_id || ''} 
                onValueChange={(value) => {
                  const franchise = franchises.find(f => f.franchise_id === value);
                  setSelectedFranchise(franchise);
                }}
              >
                <SelectTrigger className="w-full md:w-80">
                  <SelectValue placeholder="Choose a franchise to manage staff" />
                </SelectTrigger>
                <SelectContent>
                  {franchises.map((f) => (
                    <SelectItem key={f.franchise_id} value={f.franchise_id}>
                      {f.name} ({f.franchise_code || f.franchise_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedFranchise ? (
              <StaffManagementV2 franchiseId={selectedFranchise.franchise_id} isAdmin={true} />
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-muted-foreground">Select a franchise above to manage its staff</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Raw Materials Management Section */}
        {activeSection === 'raw-materials' && (
          <RawMaterialsManagement />
        )}

        {/* Raw Material Orders Section */}
        {activeSection === 'raw-material-orders' && (
          <RawMaterialOrders franchises={franchises} />
        )}
        
        {/* Cafe 1946 Expense Tracker - Full Screen */}
        {activeSection === 'cafe' && (
          <CafeExpenseTracker onBack={() => setActiveSection('franchises')} />
        )}
        </div>
      </div>

      {/* Franchise Details Dialog */}
      <Dialog open={isFranchiseDetailsOpen} onOpenChange={(open) => {
        setIsFranchiseDetailsOpen(open);
        if (!open) {
          setIsEditingFranchise(false);
          setEditFranchiseData(null);
        }
      }}>
        <DialogContent className="bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-2xl font-bold text-primary">
                {isEditingFranchise ? 'Edit Franchise' : 'Franchise Details'}
              </DialogTitle>
              {!isEditingFranchise && selectedFranchise && (
                <div className="flex gap-2">
                  <Button 
                    onClick={openAssignmentModal}
                    variant="outline"
                    className="border-green-300 text-green-600 hover:bg-green-50 hover:text-green-700"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Assign Items
                  </Button>
                  <Button 
                    onClick={startEditingFranchise}
                    variant="outline"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button 
                    onClick={openDeleteFranchiseDialog}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="delete-franchise-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {selectedFranchise && !isEditingFranchise && (
            <FranchiseDetailsView
              franchise={selectedFranchise}
              subscriptionInfo={franchiseSubscriptionInfo}
              onUpdate={async () => {
                // Refresh franchise details
                try {
                  const token = localStorage.getItem('token');
                  const franchiseId = selectedFranchise.franchise_id || selectedFranchise.id;
                  const response = await axios.get(`${API}/franchises/${franchiseId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (response.data) {
                    setSelectedFranchise(response.data);
                  }
                } catch (error) {
                  console.error('Error refreshing franchise:', error);
                }
              }}
              onChangePinClick={() => setIsChangePinOpen(true)}
              onRenewClick={() => openPaymentModal('renewal', selectedFranchise.franchise_id || selectedFranchise.id)}
            />
          )}

          {/* Edit Form */}
          {selectedFranchise && isEditingFranchise && editFranchiseData && (
            <div className="space-y-4 mt-4">
              {/* Non-editable Franchise ID */}
              <div className="bg-muted/50 border rounded-lg p-3">
                <p className="text-sm text-secondary font-medium">Franchise ID (Cannot be changed)</p>
                <p className="text-lg font-bold text-primary font-mono">{selectedFranchise.franchise_id || selectedFranchise.id}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_franchise_name">Franchise Name *</Label>
                  <Input
                    id="edit_franchise_name"
                    value={editFranchiseData.franchise_name}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, franchise_name: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_franchise_model">Franchise Model</Label>
                  <Input
                    id="edit_franchise_model"
                    value={editFranchiseData.franchise_model}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, franchise_model: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_owner_name">Owner Name *</Label>
                  <Input
                    id="edit_owner_name"
                    value={editFranchiseData.owner_name}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, owner_name: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_mobile">Contact Number</Label>
                  <Input
                    id="edit_mobile"
                    value={editFranchiseData.mobile_number}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, mobile_number: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editFranchiseData.email}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, email: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_manager">Manager Name</Label>
                  <Input
                    id="edit_manager"
                    value={editFranchiseData.manager_name}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, manager_name: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_aadhaar">Aadhaar Number</Label>
                  <Input
                    id="edit_aadhaar"
                    value={editFranchiseData.aadhaar_no}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, aadhaar_no: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_pan">PAN Number</Label>
                  <Input
                    id="edit_pan"
                    value={editFranchiseData.pan_no}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, pan_no: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="edit_address1">Address Line 1</Label>
                  <Input
                    id="edit_address1"
                    value={editFranchiseData.address_line1}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, address_line1: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="edit_address2">Address Line 2</Label>
                  <Input
                    id="edit_address2"
                    value={editFranchiseData.address_line2}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, address_line2: e.target.value})}
                    className="h-10"
                  />
                </div>
                
                {/* State Dropdown */}
                <div>
                  <Label htmlFor="edit_state">State *</Label>
                  <Select
                    value={editFranchiseData.state}
                    onValueChange={(value) => {
                      setEditFranchiseData({...editFranchiseData, state: value, district: ''});
                      fetchDistrictsForState(value);
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {statesList.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* District Dropdown */}
                <div>
                  <Label htmlFor="edit_district">District *</Label>
                  <Select
                    value={editFranchiseData.district}
                    onValueChange={(value) => setEditFranchiseData({...editFranchiseData, district: value})}
                    disabled={!editFranchiseData.state}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder={editFranchiseData.state ? "Select District" : "Select State first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {districtsList.map((district) => (
                        <SelectItem key={district} value={district}>{district}</SelectItem>
                      ))}
                      {districtsList.length === 0 && editFranchiseData.state && (
                        <SelectItem value="other">Other</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Pincode with auto-lookup */}
                <div>
                  <Label htmlFor="edit_pincode">Pincode *</Label>
                  <div className="relative">
                    <Input
                      id="edit_pincode"
                      value={editFranchiseData.pincode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setEditFranchiseData({...editFranchiseData, pincode: value});
                        if (value.length === 6) {
                          lookupPincode(value, setEditFranchiseData, {...editFranchiseData, pincode: value});
                        }
                      }}
                      placeholder="560001"
                      maxLength={6}
                      className="h-10"
                    />
                    {loadingPincode && (
                      <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                {/* City (auto-filled or manual) */}
                <div>
                  <Label htmlFor="edit_city">City *</Label>
                  <Input
                    id="edit_city"
                    value={editFranchiseData.city}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, city: e.target.value})}
                    className="h-10"
                  />
                </div>
                
                {/* Taluk (auto-filled or manual) */}
                <div>
                  <Label htmlFor="edit_taluk">Taluk</Label>
                  <Input
                    id="edit_taluk"
                    value={editFranchiseData.taluk || ''}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, taluk: e.target.value})}
                    className="h-10"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_opening_date">Date of Opening</Label>
                  <Input
                    id="edit_opening_date"
                    type="date"
                    value={editFranchiseData.date_of_opening}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, date_of_opening: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_wifi">WiFi Password</Label>
                  <Input
                    id="edit_wifi"
                    value={editFranchiseData.wifi_password}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, wifi_password: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="edit_maps">Google Maps Link</Label>
                  <Input
                    id="edit_maps"
                    value={editFranchiseData.google_maps_link}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, google_maps_link: e.target.value})}
                    className="h-10"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="edit_additional">Additional Owners/Incharge</Label>
                  <Input
                    id="edit_additional"
                    value={editFranchiseData.additional_owner_names}
                    onChange={(e) => setEditFranchiseData({...editFranchiseData, additional_owner_names: e.target.value})}
                    className="h-10"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={cancelEditFranchise} disabled={savingFranchise}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateFranchise} disabled={savingFranchise} className="bg-primary text-white">
                  {savingFranchise ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Party Ledger Details Dialog */}
      <Dialog open={isPartyLedgerDetailsOpen} onOpenChange={setIsPartyLedgerDetailsOpen}>
        <DialogContent className="bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Party Ledger Details</DialogTitle>
          </DialogHeader>
          {selectedPartyLedger && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-secondary font-medium">Party Name</p>
                  <p className="text-base font-bold text-primary">{selectedPartyLedger.party_name}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Phone Number</p>
                  <p className="text-base text-primary">{selectedPartyLedger.phone_number}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Franchise</p>
                  <p className="text-base text-primary">{selectedPartyLedger.franchise_name}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary font-medium">Current Balance</p>
                  <p className={`text-2xl font-bold ${
                    parseFloat(selectedPartyLedger.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ₹{parseFloat(selectedPartyLedger.balance).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-bold text-primary mb-3">Transaction History</h3>
                {selectedPartyLedger.transactions && selectedPartyLedger.transactions.length > 0 ? (
                  <div className="space-y-2">
                    {selectedPartyLedger.transactions.map((txn, index) => {
                      // Check if transaction is an order (description format: "Order #{order_no}")
                      const isOrder = txn.description && (txn.description.startsWith('Order #') || txn.description.startsWith('Order '));
                      const orderNo = isOrder ? txn.description.replace('Order #', '').replace('Order ', '') : (txn.order_no || null);
                      
                      // Get date from created_at or date_time (support both old and new format)
                      const txnDate = txn.created_at || txn.date_time;
                      const formattedDate = txnDate ? new Date(txnDate).toLocaleString() : 'N/A';
                      
                      // Get balance - use balance_after if available
                      const balanceAfter = txn.balance_after !== undefined && txn.balance_after !== null 
                        ? parseFloat(txn.balance_after).toFixed(2) 
                        : null;
                      
                      return (
                        <div
                          key={txn.transaction_id || index}
                          className={`p-3 bg-muted rounded-lg flex justify-between items-center ${
                            (isOrder || orderNo) ? 'cursor-pointer hover:bg-muted/70 transition-colors' : ''
                          }`}
                          onClick={() => {
                            if (orderNo) {
                              navigate(`/order/${orderNo}`);
                            }
                          }}
                        >
                          <div>
                            <p className={`font-medium text-primary ${(isOrder || orderNo) ? 'hover:underline' : ''}`}>
                              {txn.description || (orderNo ? `Order #${orderNo}` : 'Transaction')}
                              {(isOrder || orderNo) && <span className="ml-2 text-xs text-accent">(Click to view)</span>}
                            </p>
                            <p className="text-xs text-secondary">{formattedDate}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              txn.type?.toUpperCase() === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {txn.type?.toUpperCase() === 'CREDIT' ? '+' : '-'}₹{Math.abs(parseFloat(txn.amount || 0)).toFixed(2)}
                            </p>
                            <p className="text-xs text-secondary">
                              Balance: ₹{txn.balance_after !== undefined && txn.balance_after !== null 
                                ? parseFloat(txn.balance_after).toFixed(2) 
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-secondary py-4">No transactions yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Category Selection Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Select Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Existing Categories */}
            <div>
              <Label className="text-sm font-medium text-secondary">Existing Categories</Label>
              <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                {categories.length > 0 ? (
                  categories.map((cat) => (
                    <Button
                      key={cat}
                      type="button"
                      variant="outline"
                      className="w-full h-12 justify-start text-left rounded-xl hover:bg-accent hover:text-white transition-colors"
                      onClick={() => {
                        if (categoryModalFor === 'new') {
                          setNewMenuItem({ ...newMenuItem, category: cat });
                        } else {
                          setEditMenuItem({ ...editMenuItem, category: cat });
                        }
                        setIsCategoryModalOpen(false);
                        setNewCategoryName('');
                      }}
                    >
                      <span className="font-medium">{cat}</span>
                    </Button>
                  ))
                ) : (
                  <p className="text-sm text-secondary text-center py-4">No categories yet. Create one below!</p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-secondary">OR</span>
              </div>
            </div>

            {/* Create New Category */}
            <div>
              <Label className="text-sm font-medium text-secondary">Create New Category</Label>
              <div className="mt-2 flex gap-2">
                <Input
                  placeholder="Enter new category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCategoryName.trim()) {
                      e.preventDefault();
                      const trimmedCategory = newCategoryName.trim();
                      if (!categories.includes(trimmedCategory)) {
                        setCategories([...categories, trimmedCategory]);
                        if (categoryModalFor === 'new') {
                          setNewMenuItem({ ...newMenuItem, category: trimmedCategory });
                        } else {
                          setEditMenuItem({ ...editMenuItem, category: trimmedCategory });
                        }
                        setIsCategoryModalOpen(false);
                        setNewCategoryName('');
                        toast.success(`Category "${trimmedCategory}" created!`);
                      } else {
                        toast.error('Category already exists');
                      }
                    }
                  }}
                  className="h-12 rounded-xl"
                />
                <Button
                  type="button"
                  className="h-12 bg-accent hover:bg-accent/90 text-white px-6 rounded-xl font-bold"
                  onClick={() => {
                    const trimmedCategory = newCategoryName.trim();
                    if (trimmedCategory) {
                      if (!categories.includes(trimmedCategory)) {
                        setCategories([...categories, trimmedCategory]);
                        if (categoryModalFor === 'new') {
                          setNewMenuItem({ ...newMenuItem, category: trimmedCategory });
                        } else {
                          setEditMenuItem({ ...editMenuItem, category: trimmedCategory });
                        }
                        setIsCategoryModalOpen(false);
                        setNewCategoryName('');
                        toast.success(`Category "${trimmedCategory}" created!`);
                      } else {
                        toast.error('Category already exists');
                      }
                    } else {
                      toast.error('Please enter a category name');
                    }
                  }}
                >
                  Create
                </Button>
              </div>
              <p className="text-xs text-secondary mt-2">Press Enter or click Create to add new category</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Device Info Modal */}
      <DeviceInfoModal
        isOpen={isDeviceInfoOpen}
        onClose={() => setIsDeviceInfoOpen(false)}
        franchise={selectedFranchise}
      />

      {/* Change PIN Modal */}
      <Dialog open={isChangePinOpen} onOpenChange={(open) => {
        setIsChangePinOpen(open);
        if (!open) {
          setNewPin('');
          setConfirmPin('');
        }
      }}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Franchise PIN
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Franchise:</strong> {selectedFranchise?.franchise_name || selectedFranchise?.name}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Current PIN: <span className="font-mono font-bold">{selectedFranchise?.pin || selectedFranchise?.pin_code || 'N/A'}</span>
              </p>
            </div>
            
            <div>
              <Label htmlFor="newPin" className="text-sm font-medium">New PIN</Label>
              <Input
                id="newPin"
                type="password"
                placeholder="Enter new PIN (4-6 digits)"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 font-mono text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPin" className="text-sm font-medium">Confirm New PIN</Label>
              <Input
                id="confirmPin"
                type="password"
                placeholder="Re-enter new PIN"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 font-mono text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>
            
            {newPin && confirmPin && newPin !== confirmPin && (
              <p className="text-sm text-red-500">PINs do not match</p>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsChangePinOpen(false);
                  setNewPin('');
                  setConfirmPin('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePin}
                disabled={changingPin || !newPin || !confirmPin || newPin !== confirmPin}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {changingPin ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Change PIN
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Menu Items & Raw Materials Modal */}
      <Dialog open={isAssignmentModalOpen} onOpenChange={(open) => {
        if (!open && !savingAssignments) {
          closeAssignmentModal();
        }
      }}>
        <DialogContent className="bg-surface max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Package className="w-5 h-5" />
              Assign Items to Franchise
            </DialogTitle>
            {selectedFranchise && (
              <p className="text-sm text-muted-foreground">
                {selectedFranchise.name || selectedFranchise.franchise_name} ({selectedFranchise.franchise_id || selectedFranchise.id})
              </p>
            )}
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mt-2">
            <button
              onClick={() => setAssignmentTab('menu')}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                assignmentTab === 'menu' 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MenuIcon className="w-4 h-4 inline mr-2" />
              Menu Items
              <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                {selectedMenuItems.length}/{assignmentMenuItems.length}
              </span>
            </button>
            <button
              onClick={() => setAssignmentTab('materials')}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                assignmentTab === 'materials' 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              Raw Materials
              <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                {selectedRawMaterials.length}/{assignmentRawMaterials.length}
              </span>
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto py-4">
            {loadingAssignments ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Loading items...</span>
              </div>
            ) : (
              <>
                {/* Menu Items Tab */}
                {assignmentTab === 'menu' && (
                  <div className="space-y-4">
                    {/* Select All / Deselect All */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <span className="text-sm font-medium text-gray-700">
                        {selectedMenuItems.length} of {assignmentMenuItems.length} items selected
                      </span>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={selectAllMenuItems}
                          className="text-xs"
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={deselectAllMenuItems}
                          className="text-xs"
                        >
                          Deselect All
                        </Button>
                      </div>
                    </div>
                    
                    {/* Group by Category */}
                    {(() => {
                      const categories = [...new Set(assignmentMenuItems.map(item => item.category || 'Uncategorized'))].sort();
                      return categories.map(category => {
                        const categoryItems = assignmentMenuItems.filter(item => (item.category || 'Uncategorized') === category);
                        return (
                          <div key={category} className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-100 px-4 py-2 font-medium text-sm text-gray-700 flex items-center justify-between">
                              <span>{category}</span>
                              <span className="text-xs text-gray-500">
                                {categoryItems.filter(item => selectedMenuItems.includes(item.item_id)).length}/{categoryItems.length}
                              </span>
                            </div>
                            <div className="divide-y">
                              {categoryItems.map(item => (
                                <label 
                                  key={item.item_id}
                                  className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                                    !item.is_active ? 'opacity-50' : ''
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedMenuItems.includes(item.item_id)}
                                      onChange={() => toggleMenuItemSelection(item.item_id)}
                                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <div>
                                      <p className="font-medium text-sm">{item.name}</p>
                                      {!item.is_active && (
                                        <span className="text-xs text-red-500">Inactive</span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-sm text-gray-600">₹{item.price}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    
                    {assignmentMenuItems.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <MenuIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No menu items found</p>
                        <p className="text-sm">Add menu items from the Menu section first</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Raw Materials Tab */}
                {assignmentTab === 'materials' && (
                  <div className="space-y-4">
                    {/* Select All / Deselect All */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <span className="text-sm font-medium text-gray-700">
                        {selectedRawMaterials.length} of {assignmentRawMaterials.length} materials selected
                      </span>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={selectAllRawMaterials}
                          className="text-xs"
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={deselectAllRawMaterials}
                          className="text-xs"
                        >
                          Deselect All
                        </Button>
                      </div>
                    </div>
                    
                    {/* Group by Category */}
                    {(() => {
                      const categories = [...new Set(assignmentRawMaterials.map(item => item.category || 'Uncategorized'))].sort();
                      return categories.map(category => {
                        const categoryItems = assignmentRawMaterials.filter(item => (item.category || 'Uncategorized') === category);
                        return (
                          <div key={category} className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-100 px-4 py-2 font-medium text-sm text-gray-700 flex items-center justify-between">
                              <span>{category}</span>
                              <span className="text-xs text-gray-500">
                                {categoryItems.filter(item => selectedRawMaterials.includes(item.material_id)).length}/{categoryItems.length}
                              </span>
                            </div>
                            <div className="divide-y">
                              {categoryItems.map(item => (
                                <label 
                                  key={item.material_id}
                                  className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                                    !item.is_active ? 'opacity-50' : ''
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedRawMaterials.includes(item.material_id)}
                                      onChange={() => toggleRawMaterialSelection(item.material_id)}
                                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    <div>
                                      <p className="font-medium text-sm">{item.name}</p>
                                      <p className="text-xs text-gray-500">{item.unit}</p>
                                      {!item.is_active && (
                                        <span className="text-xs text-red-500">Inactive</span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-sm text-gray-600">₹{item.price}/{item.unit}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    
                    {assignmentRawMaterials.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No raw materials found</p>
                        <p className="text-sm">Add raw materials from the Raw Materials section first</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Footer */}
          <div className="border-t pt-4 flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Changes will be reflected in the franchise portal after saving
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={closeAssignmentModal}
                disabled={savingAssignments}
              >
                Cancel
              </Button>
              <Button
                onClick={saveAllAssignments}
                disabled={savingAssignments || loadingAssignments}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {savingAssignments ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Assignments
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Franchise Confirmation Modal */}
      <Dialog open={isDeleteFranchiseOpen} onOpenChange={(open) => {
        if (!open && !deletingFranchise) {
          closeDeleteFranchiseDialog();
        }
      }}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Delete Franchise
            </DialogTitle>
          </DialogHeader>
          
          {selectedFranchise && (
            <div className="space-y-4 mt-4">
              {/* Step 1: Warning */}
              {deleteConfirmStep === 1 && (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-semibold mb-2">⚠️ This action is PERMANENT and IRREVERSIBLE!</p>
                    <p className="text-sm text-red-700">
                      You are about to delete the franchise:
                    </p>
                    <p className="text-lg font-bold text-red-900 mt-2">
                      {selectedFranchise.name || selectedFranchise.franchise_name}
                    </p>
                    <p className="text-xs text-red-600 mt-1 font-mono">
                      ID: {selectedFranchise.franchise_id || selectedFranchise.id}
                    </p>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-yellow-800 mb-2">The following data will be PERMANENTLY deleted:</p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• All orders and billing history</li>
                      <li>• All staff records and attendance</li>
                      <li>• All ledger entries and transactions</li>
                      <li>• All expense records</li>
                      <li>• All inventory and raw material orders</li>
                      <li>• Device sessions</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={closeDeleteFranchiseDialog}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => setDeleteConfirmStep(2)}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700"
                    >
                      I Understand, Continue
                    </Button>
                  </div>
                </>
              )}

              {/* Step 2: Type confirmation */}
              {deleteConfirmStep === 2 && (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-semibold mb-3">
                      To confirm deletion, type the franchise name exactly:
                    </p>
                    <p className="text-lg font-bold text-red-900 bg-red-100 px-3 py-2 rounded">
                      {selectedFranchise.name || selectedFranchise.franchise_name}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-gray-600">Type franchise name to confirm:</Label>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type franchise name here..."
                      className="mt-2"
                      autoFocus
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDeleteConfirmStep(1);
                        setDeleteConfirmText('');
                      }}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setDeleteConfirmStep(3)}
                      disabled={deleteConfirmText !== (selectedFranchise.name || selectedFranchise.franchise_name)}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Confirm
                    </Button>
                  </div>
                </>
              )}

              {/* Step 3: Final confirmation */}
              {deleteConfirmStep === 3 && (
                <>
                  <div className="bg-red-100 border-2 border-red-400 rounded-lg p-6 text-center">
                    <AlertTriangle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                    <p className="text-xl font-bold text-red-800 mb-2">
                      FINAL WARNING
                    </p>
                    <p className="text-red-700">
                      Click "DELETE FOREVER" to permanently remove this franchise and ALL its data.
                    </p>
                    <p className="text-sm text-red-600 mt-3 font-semibold">
                      This cannot be undone!
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={closeDeleteFranchiseDialog}
                      className="flex-1"
                      disabled={deletingFranchise}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleDeleteFranchise}
                      disabled={deletingFranchise}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700"
                      data-testid="delete-forever-btn"
                    >
                      {deletingFranchise ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        '🗑️ DELETE FOREVER'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Franchise Subscription Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {paymentPurpose === 'new' ? 'Subscribe New Franchise' : 'Renew Subscription'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {/* Plan Info */}
            {subscriptionInfo && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <Crown className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{subscriptionInfo.plan_name}</p>
                    <p className="text-sm text-gray-500">₹{subscriptionInfo.price_per_franchise}/month per outlet</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Month Selection */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Select Duration
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 3, 6, 12].map((months) => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => setSelectedMonths(months)}
                    className={`
                      py-3 px-2 rounded-lg border-2 transition-all text-center
                      ${selectedMonths === months 
                        ? 'border-amber-500 bg-amber-50 text-amber-700' 
                        : 'border-gray-200 hover:border-amber-300 text-gray-600'}
                    `}
                  >
                    <div className="text-lg font-bold">{months}</div>
                    <div className="text-xs">{months === 1 ? 'Month' : 'Months'}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount Summary */}
            {subscriptionInfo && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Price per month</span>
                  <span>₹{subscriptionInfo.price_per_franchise}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Duration</span>
                  <span>{selectedMonths} month{selectedMonths > 1 ? 's' : ''}</span>
                </div>
                <div className="border-t border-gray-200 my-2"></div>
                <div className="flex justify-between text-lg font-bold text-gray-800">
                  <span>Total Amount</span>
                  <span className="text-amber-600">₹{calculateSubscriptionAmount()}</span>
                </div>
              </div>
            )}

            {/* Franchise Info for New */}
            {paymentPurpose === 'new' && newFranchise.franchise_name && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">New Franchise:</span> {newFranchise.franchise_name}
                </p>
              </div>
            )}

            {!subscriptionInfo && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-200 flex items-center justify-between gap-3">
                <p className="text-sm text-red-700">
                  {subscriptionInfoError || 'Loading subscription plan...'}
                </p>
                <Button type="button" variant="outline" size="sm" onClick={fetchSubscriptionInfo}>
                  Retry
                </Button>
              </div>
            )}

            {/* Pay Button */}
            <Button
              onClick={handlePayment}
              disabled={isPaymentProcessing || !subscriptionInfo}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-base rounded-lg"
            >
              {isPaymentProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay ₹{calculateSubscriptionAmount()}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-gray-400">
              Secure payment powered by Razorpay
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* FOS Watermark */}
      <FOSWatermark opacity={0.06} size="lg" />
    </div>
  );
};

export default AdminDashboard;